var Shopify = Shopify || {};
// ---------------------------------------------------------------------------
// Money format handler
// ---------------------------------------------------------------------------
Shopify.money_format = "${{amount}}";
Shopify.formatMoney = function (cents, format) {
  if (typeof cents == "string") {
    cents = cents.replace(".", "");
  }
  var value = "";
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = format || this.money_format;

  function defaultOption(opt, def) {
    return typeof opt == "undefined" ? def : opt;
  }

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultOption(precision, 2);
    thousands = defaultOption(thousands, ",");
    decimal = defaultOption(decimal, ".");

    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split("."),
      dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands),
      cents = parts[1] ? decimal + parts[1] : "";

    return dollars + cents;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
  }

  return formatString.replace(placeholderRegex, value);
};

// ANNOUNCEMENT MARQUEE
class AnnouncementMarquee extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector(".marquee_track");
    this.viewport = this.querySelector(".marquee_viewport");

    if (!this.track || !this.viewport) return;

    this.setup();
    window.addEventListener("resize", () => this.setup());
  }

  setup() {
    this.track.classList.remove("is-ready");

    const viewportWidth = this.viewport.offsetWidth;

    while (this.track.scrollWidth < viewportWidth * 2) {
      this.track.innerHTML += this.track.innerHTML;
    }

    const distance = this.track.scrollWidth / 2;

    this.track.style.setProperty("--marquee-distance", `${distance}px`);

    requestAnimationFrame(() => {
      this.track.classList.add("is-ready");
    });
  }
}

customElements.define("announcement-marquee", AnnouncementMarquee);

// REVEAL ON SCROLL
class ScrollReveal {
  constructor(selector = ".reveal", staggerDelay = 300) {
    this.selector = selector;
    this.staggerDelay = staggerDelay;

    this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
      root: null,
      threshold: 0.2,
    });

    this.initElements();
  }

  initElements(scope = document) {
    const elements = scope.querySelectorAll(this.selector);

    elements.forEach((el) => {
      if (el.dataset.revealed === "true") return;

      el.style.opacity = 0;
      el.style.transform = "translateY(20px)";
      el.style.transition = "opacity 0.4s ease, transform 0.4s ease";

      this.observer.observe(el);
    });
  }

  // Stagger reveal for a specific batch
  staggerBatch(elements) {
    elements.forEach((el, index) => {
      setTimeout(() => {
        el.style.opacity = 1;
        el.style.transform = "translateY(0)";
        el.dataset.revealed = "true";
      }, index * this.staggerDelay);

      this.observer.unobserve(el);
    });
  }

  onIntersect(entries, observer) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      const el = entry.target;

      // normal scroll-based stagger (initial load)
      if (el.classList.contains("stagger")) {
        const siblings = Array.from(
          el.parentNode.querySelectorAll(".reveal.stagger"),
        ).filter((sib) => sib.dataset.revealed !== "true");

        this.staggerBatch(siblings);
      } else {
        el.style.opacity = 1;
        el.style.transform = "translateY(0)";
        el.dataset.revealed = "true";
        observer.unobserve(el);
      }
    });
  }
}

// Initialize on DOM ready
let scrollReveal;

document.addEventListener("DOMContentLoaded", () => {
  scrollReveal = new ScrollReveal(".reveal", 150);
});

document.addEventListener("shopify:section:load", () => {
  scrollReveal = new ScrollReveal(".reveal", 150);
});

// DEBOUNCE
function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

// SWIPER
class SwiperSection extends HTMLElement {
  connectedCallback() {
    this.swiperEl = this.querySelector(".swiper");
    if (!this.swiperEl) return;

    this.waitForSwiper().then(() => this.init());
  }

  waitForSwiper() {
    return new Promise((resolve) => {
      const check = () => {
        if (window.Swiper) return resolve();
        requestAnimationFrame(check);
      };
      check();
    });
  }

  init() {
    if (this.swiper) return;

    /* ---------- read dataset ---------- */
    this.slidesDesktop = Number(this.dataset.slidesDesktop) || 1;
    this.slidesTablet = Number(this.dataset.slidesTablet) || 3;
    this.slidesMobile = Number(this.dataset.slidesMobile) || 1;
    this.slidesCustom = Number(this.dataset.slidesCustom) || 2;

    this.speed = Number(this.dataset.speed) || 600;
    this.loop = this.dataset.loop === "true";
    this.freeMode = this.dataset.freeMode === "true";
    this.effect = this.dataset.effect || "slide";
    this.gap = Number(this.dataset.gap) || 15;
    this.mobileGap = Number(this.dataset.mobileGap) || 15;
    this.centerMode = this.dataset.centerMode === "true";

    this.observeParents = this.dataset.observeParents === "true";
    this.autoHeight = this.dataset.autoHeight === "true";
    this.grabCursor = this.dataset.grabCursor === "true";
    this.watchSlidesProgress = this.dataset.progress === "true";
    this.allowTouchMove = this.dataset.allowTouchMove !== "false";

    this.paginationEl = this.querySelector(".swiper-pagination");
    this.paginationType = this.dataset.paginationType || "bullets";

    this.autoplayEnabled =
      this.dataset.autoplay === "true" || this.dataset.autoplay === "1";
    this.delay = Number(this.dataset.delay) || 3000;

    this.thumbsSelector = this.dataset.thumbs;

    /* ---------- thumbs handling ---------- */
    if (this.thumbsSelector) {
      const thumbsEl = document.querySelector(`.${this.thumbsSelector}`);

      if (thumbsEl) {
        const waitForThumbs = () => {
          if (thumbsEl.swiperInstance) {
            this.createSwiper(thumbsEl.swiperInstance);
          } else {
            requestAnimationFrame(waitForThumbs);
          }
        };
        waitForThumbs();
        return;
      }
    }

    /* ---------- no thumbs ---------- */
    this.createSwiper();
  }

  createSwiper(thumbsSwiper = null) {
    if (this.swiper) return;

    this.swiper = new Swiper(this.swiperEl, {
      slidesPerView: this.slidesMobile,
      spaceBetween: this.gap,
      speed: this.speed,
      loop: this.loop,
      freeMode: this.freeMode,
      centeredSlides: this.centerMode,
      grabCursor: this.grabCursor,
      effect: this.effect,

      observeParents: this.observeParents,
      autoHeight: this.autoHeight,
      grabCursor: this.grabCursor,
      watchSlidesProgress: this.watchSlidesProgress,
      allowTouchMove: this.allowTouchMove,

      breakpoints: {
        0: {
          slidesPerView: this.slidesMobile,
          spaceBetween: this.mobileGap,
          freeMode: false,
        },
        640: { slidesPerView: this.slidesCustom },
        899: { slidesPerView: this.slidesTablet },
        1199: { slidesPerView: this.slidesDesktop },
      },

      ...(this.autoplayEnabled && {
        autoplay: {
          delay: this.delay,
          disableOnInteraction: false,
        },
      }),

      ...(this.paginationEl && {
        pagination: {
          el: this.paginationEl,
          clickable: true,
          type: this.paginationType,
          renderBullet: function (index, className) {
            return `
              <span class="${className}">
                <svg
                  class="progress-ring"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="progress-ring__bg"
                    stroke-width="2"
                    fill="transparent"
                    r="10"
                    cx="12"
                    cy="12"
                  />
                  <circle
                    class="progress-ring__progress"
                    stroke-width="2"
                    fill="transparent"
                    r="10"
                    cx="12"
                    cy="12"
                  />
                </svg>
              </span>
            `;
          },
        },
      }),

      on: {
        slideChangeTransitionStart() {
          document.querySelectorAll(".progress-ring__progress").forEach((circle) => {
            circle.style.animation = "none";
            circle.getBoundingClientRect();
            circle.style.animation = "";
          });
        },
        init: () => {
          window.dispatchEvent(new Event("layout:updated"));
        },
        resize: () => {
          window.dispatchEvent(new Event("layout:updated"));
        },
      },

      ...(thumbsSwiper && {
        thumbs: {
          swiper: thumbsSwiper,
        },
      }),

      navigation: {
        nextEl: this.querySelector(".swiper-button-next"),
        prevEl: this.querySelector(".swiper-button-prev"),
      }
    });

    /* expose instance for thumbs linking */
    this.swiperEl.swiperInstance = this.swiper;

    this.swiperEl.style.setProperty(
      "--swiper-autoplay-duration",
      `${this.delay}ms`,
    );
  }

  disconnectedCallback() {
    if (!this.swiper) return;
    this.swiper.destroy(true, true);
    this.swiper = null;
  }
}

customElements.define("swiper-section", SwiperSection);


// MOBILE MENU DRAWER
class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.parentLists = this.querySelectorAll(".mobile_parent_li.has_children");
    this.childLists = this.querySelectorAll(".mobile_child_li.has_children");
  }

  connectedCallback() {
    if (this.parentLists.length) {
      this.parentLists.forEach((list) => {
        const btn = list.querySelector("button.parent_li_btn");
        if (!btn) return;
        const icon = btn.querySelector("svg");
        btn.addEventListener("click", () => this.handleParentBtnClick(list, icon));
      });
    }

    if (this.childLists.length) {
      this.childLists.forEach((list) => {
        const btn = list.querySelector("button.child_li_btn");
        if (!btn) return;
        const child_link = btn.previousElementSibling;
        const icon = btn.querySelector("svg");
        btn.addEventListener("click", () => this.handleChildBtnClick(list, icon, child_link));
      })
    }
  }

  handleChildBtnClick(list, icon, child_link) {
    const wrapper = list.querySelector('.mobile_grandchild_ul_wrapper');
    if (!wrapper) return;

    if (child_link) {
      if (child_link.classList.contains('active')) {
        child_link.classList.remove('active')
      } else {
        child_link.classList.add('active')
      }
    }

    const isOpen = wrapper.classList.toggle('active');
    icon.classList.toggle('rotate', isOpen);

    if (isOpen) {
      wrapper.removeAttribute('inert');
      wrapper.setAttribute('aria-hidden', 'false');
    } else {
      wrapper.setAttribute('inert', '');
      wrapper.setAttribute('aria-hidden', 'true');
    }

  }

  handleParentBtnClick(list, icon) {
    const wrapper = list.querySelector('.mobile_child_ul_wrapper');
    if (!wrapper) return;

    const isOpen = wrapper.classList.toggle('active');
    icon.classList.toggle('rotate', isOpen);

    if (isOpen) {
      wrapper.removeAttribute('inert');
      wrapper.setAttribute('aria-hidden', 'false');
    } else {
      wrapper.setAttribute('inert', '');
      wrapper.setAttribute('aria-hidden', 'true');
    }

  }
}

customElements.define("menu-drawer", MenuDrawer);

// HERO BANNER
class HeroBanner extends HTMLElement {
  constructor() {
    super();

    this.slides = [];
    this.thumbs = [];
    this.activeIndex = 0;
    this.isAnimating = false;

    this.bannerInner = null;
    this.bgTexts = [];

    this.marqueeShownOnce = false;
  }

  connectedCallback() {
    this.bannerInner = this.querySelector(".hero_banner_inner");
    this.bgTexts = Array.from(this.querySelectorAll(".hero_banner_bg_text h2"));

    this.slides = Array.from(this.querySelectorAll(".hero_slide_block"));

    this.thumbs = Array.from(this.querySelectorAll(".hero_banner_thumbs"));

    if (!this.slides.length) return;

    this.init();

    this.usingKeyboard = false;

    document.addEventListener("keydown", (e) => {
      if (e.key === "Tab") {
        this.usingKeyboard = true;
      }
    });

    document.addEventListener("mousedown", () => {
      this.usingKeyboard = false;
    });
  }

  init() {
    this.slides.forEach((slide) => slide.classList.remove("is-active"));

    this.syncActiveThumb();
    this.showSlide(0, true);
    this.revealMarqueeOnce();

    requestAnimationFrame(() => {
      this.classList.add("is-loaded");
    });

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => {
        const index = Number(thumb.dataset.index);
        if (index === this.activeIndex || this.isAnimating) return;
        this.showSlide(index);
      });
    });
  }

  showSlide(index, instant = false) {
    const current = this.slides[this.activeIndex];
    const next = this.slides[index];
    if (!next) return;

    this.isAnimating = true;

    if (current) {
      current.classList.remove("is-active");
      this.classList.remove("has-active-slide");

      const current_anchor = current.querySelector(".hero_slide_btn a");
      const current_thumbsBtn = current.querySelectorAll(
        ".hero_banner_thumbs button",
      );

      if (current_anchor) {
        current_anchor.setAttribute("tabindex", "-1");
      }

      if (current_thumbsBtn.length) {
        current_thumbsBtn.forEach((btn) => {
          btn.setAttribute("tabindex", "-1");
        });
      }
    }

    this.updateBackground(next);
    this.updateBgTextColor(next);

    const activate = () => {
      next.classList.add("is-active");
      this.classList.add("has-active-slide");

      this.activeIndex = index;

      this.syncActiveThumb();

      const anchor = next.querySelector(".hero_slide_btn a");
      if (anchor) anchor.setAttribute("tabindex", "0");

      const thumbsBtn = next.querySelectorAll(".hero_banner_thumbs button");
      thumbsBtn.forEach((btn) => {
        btn.setAttribute("tabindex", "0");
      });

      const activeThumb = next.querySelector(
        ".hero_banner_thumbs.is-active button",
      );

      if (activeThumb) {
        if (this.usingKeyboard) {
          requestAnimationFrame(() => {
            activeThumb.focus({ preventScroll: true });
          });
        }
      }

      this.isAnimating = false;
    };

    if (instant) {
      activate();
    } else {
      setTimeout(activate, 30);
    }
  }

  syncActiveThumb() {
    this.thumbs.forEach((thumb) => {
      const index = Number(thumb.dataset.index);
      thumb.classList.toggle("is-active", index === this.activeIndex);
    });
  }

  revealMarqueeOnce() {
    if (this.marqueeShownOnce) return;

    setTimeout(() => {
      this.classList.add("is-marquee-visible");
      this.marqueeShownOnce = true;
    }, 500);
  }

  updateBackground(slide) {
    const bg = slide.dataset.bg;
    if (!bg || !this.bannerInner) return;
    this.bannerInner.style.backgroundColor = bg;
  }

  updateBgTextColor(slide) {
    const headColor = slide.dataset.head;
    if (!headColor) return;

    const bgWrapper = this.querySelector(".hero_banner_bg_text");
    if (!bgWrapper) return;

    bgWrapper.style.setProperty("--bg-text-color", headColor);
  }
}

customElements.define("hero-banner", HeroBanner);

// SCROLL-LIST
class ScrollList extends HTMLElement {
  constructor() {
    (super(),
      (this.listContainer = this.querySelector(".scroll_list")),
      (this.scrollDirection = this.getAttribute("data-direction") || "left"),
      (this.speed = parseInt(this.getAttribute("data-speed")) || 40),
      (this.currentTrans = 0),
      (this.lastWindowWidth = window.innerWidth),
      this.observeInView(),
      (window.Shopify.designMode || window.debug) &&
      ((this.debounceWindowSizeChangeHandler = debounce(
        this.onWindowSizeChange.bind(this),
        500,
      )),
        window.addEventListener(
          "resize",
          this.debounceWindowSizeChangeHandler,
        )));
  }

  disconnectedCallback() {
    this.debounceWindowSizeChangeHandler &&
      window.removeEventListener(
        "resize",
        this.debounceWindowSizeChangeHandler,
      );
  }

  connectedCallback() {
    let images = this.querySelectorAll("img");
    let wrapper = this.closest(".scroll_list_wrapper");
    let height = wrapper.getBoundingClientRect().height;
    wrapper.parentElement.style.setProperty("--height", `${height}px`);
  }

  init() {
    if ((this.getAllItems(), this.items.length < 1)) return;

    (this.getOriginalTotalWidth(),
      this.cloneItems(),
      this.getAllItems(),
      this.getGap());

    const transformDistance = this.originalWidth + this.gap;

    if (this.scrollDirection === "right") {
      this.style.setProperty("--from-x", "0");
      this.style.setProperty("--end-x", `-${transformDistance}px`);
    } else {
      this.style.setProperty("--from-x", `-${transformDistance}px`);
      this.style.setProperty("--end-x", "0");
    }

    this.setAttribute("data-init", "true");
  }

  onWindowSizeChange() {
    const currentWidth = window.innerWidth;
    currentWidth !== this.lastWindowWidth &&
      ((this.lastWindowWidth = currentWidth),
        this.hasAttribute("data-init") &&
        (this.removeAttribute("data-init"),
          setTimeout(() => {
            (this.classList.add("hidden"),
              this.querySelectorAll(".item-clone").forEach((item) =>
                item.remove(),
              ),
              this.classList.remove("hidden"),
              this.init());
          })));
  }

  observeInView() {
    new IntersectionObserver(
      (entries, observer2) => {
        entries[0].isIntersecting && (this.init(), observer2.disconnect());
      },
      {
        root: null,
        rootMargin: "100px 0px 100px 0px",
      },
    ).observe(this);
  }

  cloneItems() {
    const times = Math.ceil(this.clientWidth / this.originalWidth);
    let fragment = document.createDocumentFragment();
    for (let i = 0; i < times; i++)
      this.items.forEach((item) => {
        const cloneItem = item.cloneNode(!0);
        (cloneItem.removeAttribute("data-shopify-editor-block"),
          cloneItem.classList.add("item-clone"),
          fragment.appendChild(cloneItem));
      });
    this.listContainer.appendChild(fragment);
  }
  getAllItems() {
    this.items = Array.from(this.querySelectorAll(".scroll_list_item"));
  }
  getOriginalTotalWidth() {
    this.originalWidth =
      this.items[this.items.length - 1].offsetLeft -
      this.items[0].offsetLeft +
      this.items[this.items.length - 1].offsetWidth;
  }
  getGap() {
    this.items.length < 2
      ? (this.gap = 32)
      : (this.gap =
        this.items[1].offsetLeft -
        this.items[0].offsetLeft -
        this.items[0].offsetWidth);
  }
  moveItemVisible(item) {
    if (this.items.indexOf(item) === -1) return;
    const containerRect = this.getBoundingClientRect(),
      itemRect = item.getBoundingClientRect();
    let offset;
    if (itemRect.right > containerRect.right)
      offset = containerRect.right - itemRect.right;
    else if (itemRect.left < containerRect.left)
      offset = containerRect.left - itemRect.left;
    else return;
    ((this.currentTrans = this.currentTrans + offset),
      (this.listContainer.style.transform = `translateX(${this.currentTrans}px)`));
  }
}

customElements.define("scroll-list", ScrollList);

window.addEventListener("scroll", () => {
  document.dispatchEvent(new Event("theme:scroll"));
});

window.addEventListener("resize", () => {
  document.dispatchEvent(new Event("theme:resize"));
});

// COLLECTION SLIDER
class CollectionSlider extends HTMLElement {
  constructor() {
    super();
    this.containerWidth = parseInt(this.dataset.container, 10);
  }

  connectedCallback() {
    this.handleScroll = this.handleScroll.bind(this);

    this.handleScroll();

    window.addEventListener("scroll", this.handleScroll);
    window.addEventListener("resize", this.handleScroll);

    const slider = this.querySelector(".collection_slider_inner");

    if (slider) {
      slider.classList.add(
        `direction-${this.dataset.directionStyle || "forward"}`
      );
    }
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleScroll);
  }

  handleScroll() {
    const windowWidth = window.innerWidth;
    const margin = Math.max(0, windowWidth - this.containerWidth);

    this.style.setProperty("--margin-left", `${margin}px`);
  }
}

customElements.define("collection-slider", CollectionSlider);

// IMAGE HOTSPOTS
class ImageHotspots extends HTMLElement {
  constructor() {
    super();

    this.hotspots = this.querySelectorAll(".image_hotspot");
    this.popups = this.querySelectorAll(".image_hotspot_block_popup");
  }

  connectedCallback() {
    if (!this.hotspots.length) return;

    this.hotspots.forEach((hotspot) => {
      hotspot.addEventListener("click", (e) => {
        e.stopPropagation();

        const index = hotspot.dataset.index;

        this.closeAll();

        const activePopup = this.querySelector(
          `.image_hotspot_block_popup[data-index="${index}"]`,
        );

        if (activePopup) {
          activePopup.classList.add("active");         
        }
      });
    });
  }
}

customElements.define("image-hotspots", ImageHotspots);

// COLLECTION TABS
class CollectionTabs extends HTMLElement {
  constructor() {
    super();
    this.tabs = [...this.querySelectorAll(".collection_tabs_block")];
    this.panels = [
      ...this.querySelectorAll(".collection_tabs_block_tab_panel"),
    ];
    this.activeIndex = null;
  }

  connectedCallback() {
    this.addEventListener("click", this.onTabClick.bind(this));
  }

  onTabClick(e) {
    const tab = e.target.closest(".collection_tabs_block");
    if (!tab) return;

    const index = tab.dataset.tabIndex;
    if (index === this.activeIndex) return;

    this.activeIndex = index;

    this.updateTabs(index);
    this.updatePanels(index);
  }

  updateTabs(index) {
    this.tabs.forEach((tab) => {
      tab.classList.toggle("active", tab.dataset.tabIndex === index);
    });
  }

  updatePanels(index) {
    this.panels.forEach((panel) => {
      const isActive = panel.dataset.panelIndex === index;

      if (isActive) {
        panel.classList.add("active", "in_view");
        panel.removeAttribute("inert");

        requestAnimationFrame(() => {
          panelObserver.observe(panel);
        });
      } else {
        panel.classList.remove("active", "in_view");
        panel.setAttribute("inert", "");
      }
    });
  }
}

customElements.define("collection-tabs", CollectionTabs);

const panelObserver = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      const panel = entry.target;

      if (panel.classList.contains("active")) {
        panel.classList.add("in_view");
      }
    });
  },
  { threshold: 0.3 },
);

document
  .querySelectorAll(".collection_tabs_block_tab_panel")
  .forEach((panel) => {
    panelObserver.observe(panel);
  });

document.addEventListener("shopify:section:load", () => {
  document
    .querySelectorAll(".collection_tabs_block_tab_panel")
    .forEach((panel) => {
      panelObserver.observe(panel);
    });
});


// COUNTDOWN TIMER
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.dateString = this.dataset.date;
    this.items = {
      days: this.querySelector(".cw_days h3 span"),
      hours: this.querySelector(".cw_hours h3 span"),
      minutes: this.querySelector(".cw_minutes h3 span"),
      seconds: this.querySelector(".cw_seconds h3 span"),
    };

    this.endTime = this.parseDate(this.dateString);

    if (isNaN(this.endTime)) {
      this.setZero();
      return;
    }

    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  parseDate(value) {
    if (!value) return NaN;

    const parts = value.split(/[- :]/);
    if (parts.length < 6) return NaN;

    const [dd, mm, yyyy, hh, min, ss] = parts.map(Number);

    return new Date(yyyy, mm - 1, dd, hh, min, ss).getTime();
  }

  tick() {
    const now = Date.now();
    const diff = this.endTime - now;

    if (diff <= 0 || isNaN(diff)) {
      this.setZero();
      clearInterval(this.interval);
      return;
    }

    const time = {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };

    Object.keys(this.items).forEach((key) => {
      this.updateValue(this.items[key], time[key]);
    });
  }

  updateValue(el, value) {
    const formatted = String(value).padStart(2, "0");

    if (el.textContent !== formatted) {
      el.classList.add("is-updating");

      setTimeout(() => {
        el.textContent = formatted;
        el.classList.remove("is-updating");
      }, 180);
    }
  }

  setZero() {
    Object.values(this.items).forEach((el) => {
      el.textContent = "00";
      el.classList.remove("is-updating");
    });
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }
}

customElements.define("countdown-timer", CountdownTimer);

// VIDEO SECTION
class VideoSection extends HTMLElement {
  constructor() {
    super();

    this.playBtn = this.querySelector(".play_icon_wrapper");
    this.imgWrapper = this.querySelector(".video_section_poster_image_wrapper");
    this.vdoWrapper = this.querySelector(".video_section_video_wrapper");
    this.video = this.querySelector("video");
    this.iframe = this.querySelector("iframe");

    this.handleClick = this.handleClick.bind(this);
    this.handleVdoClick = this.handleVdoClick.bind(this);
  }

  connectedCallback() {
    if (this.playBtn) {
      this.playBtn.addEventListener("click", this.handleClick);
    }

    if (this.video) {
      this.video.addEventListener("click", this.handleVdoClick);
    }
  }

  handleClick() {
    if (!this.imgWrapper || !this.vdoWrapper) return;

    this.imgWrapper.classList.add("hide");
    this.vdoWrapper.classList.add("show");

    if (this.video) {
      this.video.setAttribute("tabIndex", "0");
      this.video.play();
      return;
    }

    if (!this.iframe) return;

    this.iframe.setAttribute("tabIndex", "0");
    const source = this.iframe.getAttribute("src") || "";

    if (source.includes("youtube.com") || source.includes("youtu.be")) {
      this.iframe.contentWindow.postMessage(
        '{"event":"command","func":"playVideo","args":""}',
        "*",
      );
    } else if (source.includes("vimeo.com")) {
      this.iframe.contentWindow.postMessage(
        '{"method":"play"}',
        "https://player.vimeo.com",
      );
    }
  }

  handleVdoClick() {
    if (
      !this.vdoWrapper ||
      this.vdoWrapper.classList.contains("controls_enabled")
    ) {
      return;
    }

    if (this.video.paused) {
      this.video.play();
    } else {
      this.video.pause();
    }
  }
}

customElements.define("video-section", VideoSection);

// FOOTER
class FooterSection extends HTMLElement {
  constructor() {
    super();
    this.footerLogo = this.querySelector(".footer_bottom_logo");
    this.handleScroll = this.handleScroll.bind(this);
  }

  connectedCallback() {
    if (!this.footerLogo) return;

    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.handleScroll);

    // run once on load
    this.handleScroll();
  }

  handleScroll() {
    const scrollTop = window.scrollY;
    const viewportHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;

    const scrollPercent =
      (scrollTop + viewportHeight) / documentHeight;

    if (scrollPercent >= 0.96) {
      this.footerLogo.classList.add("is-visible");
    } else {
      this.footerLogo.classList.remove("is-visible");
    }
  }
}

customElements.define("footer-section", FooterSection);

// DRAWERS
const drawerOpenBtns = document.querySelectorAll(".drawer_open_btn");
const drawerCloseBtns = document.querySelectorAll(".drawer_close_btn");
const drawers = document.querySelectorAll(".main_drawer_wrapper");
let scrollPosition = 0;

let lastFocusedButton = null;

drawers.forEach((drawer) => {
  const focusableElements = drawer.querySelectorAll(
    "button, input:not([type='hidden']), a, select, textarea",
  );
  focusableElements.forEach((el) => {
    el.setAttribute("tabindex", "-1");
  });
});

function closeAllDrawers() {
  drawers.forEach((drawer) => {
    drawer.classList.remove("active");
    drawer.setAttribute("aria-hidden", "true");
    drawer.setAttribute("inert", "");
  });
}

function trapFocus(event, drawer) {
  const focusableElements = drawer.querySelectorAll(
    "button, input:not([type='hidden']), a, select, textarea",
  );
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (event.key === "Tab") {
    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }
}

function openDrawer(drawer, button, section) {
  closeAllDrawers();
  if (drawer) {
    if (!lastFocusedButton && button) {
      lastFocusedButton = button;
    }

    drawer.classList.add("active");
    drawer.removeAttribute("inert");
    drawer.setAttribute("aria-hidden", "false");

    const focusableElements = drawer.querySelectorAll(
      "button, input:not([type='hidden']), a, select, textarea",
    );
    focusableElements.forEach((el) => {
      el.setAttribute("tabindex", "0");
    });

    if (focusableElements.length) {
      focusableElements[0].focus();
    }

    drawer.addEventListener("keydown", (event) => trapFocus(event, drawer));
  }
}

function closeDrawer(drawer, section) {
  if (drawer) {
    drawer.classList.remove("active");
    drawer.setAttribute("inert", "");
    drawer.setAttribute("aria-hidden", "true");

    const focusableElements = drawer.querySelectorAll("input, button, a");
    focusableElements.forEach((el) => {
      el.setAttribute("tabindex", "-1");
    });

    const anyDrawerOpen = document.querySelector(".main_drawer_wrapper.active");

    if (!anyDrawerOpen && lastFocusedButton) {
      lastFocusedButton.focus();
      lastFocusedButton = null;
    }
  }
}

function AddScrollLock() {
  setTimeout(() => {
    document.body.setAttribute("data-scroll-locked", "");
    document.body.classList.add("overflow-hidden");
    lenis.stop();
  }, 300);
}

function RemoveScrollLock() {
  setTimeout(() => {
    document.body.removeAttribute("data-scroll-locked");
    document.body.classList.remove("overflow-hidden");
    lenis.start();
  }, 300);
}

function initDrawerOpenButtons() {
  const openBtns = document.querySelectorAll("[data-drawer]");

  openBtns.forEach((button) => {
    if (!button.hasAttribute("data-drawer-initialized")) {
      button.setAttribute("data-drawer-initialized", "true");

      button.addEventListener("click", async (event) => {
        event.preventDefault();
        const drawerId = button.getAttribute("data-drawer");
        const drawer = document.getElementById(drawerId);
        const rootSection = drawer.dataset.rootSection === "true";
        const section = document.querySelector(".main-product-section");

        if (drawer) {
          if (rootSection && section) {
            openDrawer(drawer, button, section);
            AddScrollLock();
          } else {
            openDrawer(drawer, button);
            AddScrollLock();
          }
        }
      });
    }
  });
}

document.addEventListener("DOMContentLoaded", initDrawerOpenButtons);
document.addEventListener("shopify:section:load", initDrawerOpenButtons);
document.addEventListener("shopify:section:unload", initDrawerOpenButtons);
document.addEventListener("collection-grid-update", initDrawerOpenButtons);

function bindDrawerCloseButtons() {
  document.querySelectorAll(".drawer_close_btn").forEach((button) => {
    if (button.dataset.closeInitialized) return; // avoid rebinding
    button.dataset.closeInitialized = "true";

    button.addEventListener("click", () => {
      const drawer = button.closest(".main_drawer_wrapper");
      const section = document.querySelector(".main-product-section");
      if (section) {
        closeDrawer(drawer, section);
        RemoveScrollLock();
      } else {
        closeDrawer(drawer);
        RemoveScrollLock();
      }
    });
  });
}

document.addEventListener("DOMContentLoaded", bindDrawerCloseButtons);
document.addEventListener("shopify:section:load", bindDrawerCloseButtons);
document.addEventListener("collection-grid-update", bindDrawerCloseButtons);

drawers.forEach((drawer) => {
  drawer.addEventListener("click", (event) => {
    const section = document.querySelector(".main-product-section");
    if (event.target === drawer) {
      if (section) {
        closeDrawer(drawer, section);
        RemoveScrollLock();
      } else {
        closeDrawer(drawer);
        RemoveScrollLock();
      }
    }
  });

  drawer.addEventListener("keydown", (event) => {
    const section = document.querySelector(".main-product-section");
    if (event.key === "Escape") {
      if (section) {
        closeDrawer(drawer, section);
        RemoveScrollLock();
      } else {
        closeDrawer(drawer);
        RemoveScrollLock();
      }
    }
  });
});

// SEARCH DRAWER
class SearchDrawer extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector("#search_drawer_input");
    this.reset = this.querySelector(".search_drawer_reset");

    this.emptyWrapper = this.querySelector(".empty_search_drawer");
    this.resultsWrapper = this.querySelector(".search_drawer_results_wrapper");
    this.resultsInner = this.querySelector(".search_drawer_results_inner");
  }

  connectedCallback() {
    if (!this.input) return;

    this.input.addEventListener(
      "input",
      this.debounce(this.handleInput.bind(this), 300),
    );

    this.input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        const q = this.input.value.trim();
        if (q) {
          window.location.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(q)}&options[prefix]=last`;
        }
      }
    });

    this.reset?.addEventListener("click", this.handleReset.bind(this));
  }

  /* --------------------------
     Utils
  -------------------------- */

  debounce(fn, delay = 300) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  toggleReset(show) {
    if (!this.reset) return;
    this.reset.classList.toggle("hidden", !show);
  }

  toggleView(showEmpty) {
    if (this.emptyWrapper) {
      this.emptyWrapper.style.display = showEmpty ? "block" : "none";
    }
    if (this.resultsWrapper) {
      this.resultsWrapper.style.display = showEmpty ? "none" : "block";
    }
  }

  /* --------------------------
     Handlers
  -------------------------- */

  handleReset(e) {
    e.preventDefault();
    this.input.value = "";
    this.toggleReset(false);
    this.toggleView(true);
    this.resultsInner.innerHTML = "";
    this.classList.remove("is-loading");
  }

  handleInput() {
    const q = this.input.value.trim();

    this.toggleReset(q.length > 0);

    if (!q) {
      this.toggleView(true);
      return;
    }

    this.setLoading(true);

    fetch(
      `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,collection,article,page,query`,
    )
      .then((res) => res.json())
      .then((data) => {
        this.setLoading(false);
        this.renderResults(data.resources?.results || {});
      })
      .catch((err) => {
        this.setLoading(false);
        console.error("Search error:", err);
      });
  }

  /* --------------------------
     Loader
  -------------------------- */

  setLoading(isLoading) {
    this.classList.toggle("is-loading", isLoading);

    if (isLoading) {
      this.toggleView(false);
      this.resultsInner.innerHTML = this.getLoadingMarkup();
    }
  }

  getLoadingMarkup() {
    return `
      <div class="search_loading">
        <div class="search_skeleton"></div>
        <div class="search_skeleton"></div>
        <div class="search_skeleton"></div>
      </div>
    `;
  }

  /* --------------------------
     Render
  -------------------------- */

  renderResults(results) {
    const {
      products = [],
      collections = [],
      articles = [],
      pages = [],
      queries = [],
    } = results;

    this.toggleView(false);
    this.resultsInner.innerHTML = "";

    const addGroup = (title, html) => {
      const group = document.createElement("div");
      group.className = "search_group";
      group.innerHTML = `<h6>${title}</h6>${html}`;
      this.resultsInner.appendChild(group);
    };

    if (queries.length) {
      addGroup(
        "Suggestions",
        queries
          .map((q) => `<p><a href="${q.url}">${q.styled_text}</a></p>`)
          .join(""),
      );
    }

    if (collections.length) {
      addGroup(
        "Collections",
        collections
          .map((c) => `<p><a href="${c.url}">${c.title}</a></p>`)
          .join(""),
      );
    }

    if (products.length) {
      addGroup(
        "Products",
        products
          .map(
            (p) => `
            <div class="search_result_product_card">
              <div class="search_result_product_card_img ratio" style="--aspect-ratio: 100%;">
                <a href="${p.url}">
                  ${p.featured_image?.url 
                    ? `<img src="${p.featured_image.url}" alt="${p.title}">`
                    : `<svg class="placeholder-svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M375.5 345.2c0-.1 0-.1 0 0 0-.1 0-.1 0 0-1.1-2.9-2.3-5.5-3.4-7.8-1.4-4.7-2.4-13.8-.5-19.8 3.4-10.6 3.6-40.6 1.2-54.5-2.3-14-12.3-29.8-18.5-36.9-5.3-6.2-12.8-14.9-15.4-17.9 8.6-5.6 13.3-13.3 14-23 0-.3 0-.6.1-.8.4-4.1-.6-9.9-3.9-13.5-2.1-2.3-4.8-3.5-8-3.5h-54.9c-.8-7.1-3-13-5.2-17.5-6.8-13.9-12.5-16.5-21.2-16.5h-.7c-8.7 0-14.4 2.5-21.2 16.5-2.2 4.5-4.4 10.4-5.2 17.5h-48.5c-3.2 0-5.9 1.2-8 3.5-3.2 3.6-4.3 9.3-3.9 13.5 0 .2 0 .5.1.8.7 9.8 5.4 17.4 14 23-2.6 3.1-10.1 11.7-15.4 17.9-6.1 7.2-16.1 22.9-18.5 36.9-2.2 13.3-1.2 47.4 1 54.9 1.1 3.8 1.4 14.5-.2 19.4-1.2 2.4-2.3 5-3.4 7.9-4.4 11.6-6.2 26.3-5 32.6 1.8 9.9 16.5 14.4 29.4 14.4h176.8c12.9 0 27.6-4.5 29.4-14.4 1.2-6.5-.5-21.1-5-32.7zm-97.7-178c.3-3.2.8-10.6-.2-18 2.4 4.3 5 10.5 5.9 18h-5.7zm-36.3-17.9c-1 7.4-.5 14.8-.2 18h-5.7c.9-7.5 3.5-13.7 5.9-18zm4.5-6.9c0-.1.1-.2.1-.4 4.4-5.3 8.4-5.8 13.1-5.8h.7c4.7 0 8.7.6 13.1 5.8 0 .1 0 .2.1.4 3.2 8.9 2.2 21.2 1.8 25h-30.7c-.4-3.8-1.3-16.1 1.8-25zm-70.7 42.5c0-.3 0-.6-.1-.9-.3-3.4.5-8.4 3.1-11.3 1-1.1 2.1-1.7 3.4-2.1l-.6.6c-2.8 3.1-3.7 8.1-3.3 11.6 0 .2 0 .5.1.8.3 3.5.9 11.7 10.6 18.8.3.2.8.2 1-.2.2-.3.2-.8-.2-1-9.2-6.7-9.8-14.4-10-17.7 0-.3 0-.6-.1-.8-.3-3.2.5-7.7 3-10.5.8-.8 1.7-1.5 2.6-1.9h155.7c1 .4 1.9 1.1 2.6 1.9 2.5 2.8 3.3 7.3 3 10.5 0 .2 0 .5-.1.8-.3 3.6-1 13.1-13.8 20.1-.3.2-.5.6-.3 1 .1.2.4.4.6.4.1 0 .2 0 .3-.1 13.5-7.5 14.3-17.5 14.6-21.3 0-.3 0-.5.1-.8.4-3.5-.5-8.5-3.3-11.6l-.6-.6c1.3.4 2.5 1.1 3.4 2.1 2.6 2.9 3.5 7.9 3.1 11.3 0 .3 0 .6-.1.9-1.5 20.9-23.6 31.4-65.5 31.4h-43.8c-41.8 0-63.9-10.5-65.4-31.4zm91 89.1h-7c0-1.5 0-3-.1-4.2-.2-12.5-2.2-31.1-2.7-35.1h3.6c.8 0 1.4-.6 1.4-1.4v-14.1h2.4v14.1c0 .8.6 1.4 1.4 1.4h3.7c-.4 3.9-2.4 22.6-2.7 35.1v4.2zm65.3 11.9h-16.8c-.4 0-.7.3-.7.7 0 .4.3.7.7.7h16.8v2.8h-62.2c0-.9-.1-1.9-.1-2.8h33.9c.4 0 .7-.3.7-.7 0-.4-.3-.7-.7-.7h-33.9c-.1-3.2-.1-6.3-.1-9h62.5v9zm-12.5 24.4h-6.3l.2-1.6h5.9l.2 1.6zm-5.8-4.5l1.6-12.3h2l1.6 12.3h-5.2zm-57-19.9h-62.4v-9h62.5c0 2.7 0 5.8-.1 9zm-62.4 1.4h62.4c0 .9-.1 1.8-.1 2.8H194v-2.8zm65.2 0h7.3c0 .9.1 1.8.1 2.8H259c.1-.9.1-1.8.1-2.8zm7.2-1.4h-7.2c.1-3.2.1-6.3.1-9h7c0 2.7 0 5.8.1 9zm-7.7-66.7v6.8h-9v-6.8h9zm-8.9 8.3h9v.7h-9v-.7zm0 2.1h9v2.3h-9v-2.3zm26-1.4h-9v-.7h9v.7zm-9 3.7v-2.3h9v2.3h-9zm9-5.9h-9v-6.8h9v6.8zm-119.3 91.1c-2.1-7.1-3-40.9-.9-53.6 2.2-13.5 11.9-28.6 17.8-35.6 5.6-6.5 13.5-15.7 15.7-18.3 11.4 6.4 28.7 9.6 51.8 9.6h6v14.1c0 .8.6 1.4 1.4 1.4h5.4c.3 3.1 2.4 22.4 2.7 35.1 0 1.2.1 2.6.1 4.2h-63.9c-.8 0-1.4.6-1.4 1.4v16.1c0 .8.6 1.4 1.4 1.4H256c-.8 11.8-2.8 24.7-8 33.3-2.6 4.4-4.9 8.5-6.9 12.2-.4.7-.1 1.6.6 1.9.2.1.4.2.6.2.5 0 1-.3 1.3-.8 1.9-3.7 4.2-7.7 6.8-12.1 5.4-9.1 7.6-22.5 8.4-34.7h7.8c.7 11.2 2.6 23.5 7.1 32.4.2.5.8.8 1.3.8.2 0 .4 0 .6-.2.7-.4 1-1.2.6-1.9-4.3-8.5-6.1-20.3-6.8-31.1H312l-2.4 18.6c-.1.4.1.8.3 1.1.3.3.7.5 1.1.5h9.6c.4 0 .8-.2 1.1-.5.3-.3.4-.7.3-1.1l-2.4-18.6H333c.8 0 1.4-.6 1.4-1.4v-16.1c0-.8-.6-1.4-1.4-1.4h-63.9c0-1.5 0-2.9.1-4.2.2-12.7 2.3-32 2.7-35.1h5.2c.8 0 1.4-.6 1.4-1.4v-14.1h6.2c23.1 0 40.4-3.2 51.8-9.6 2.3 2.6 10.1 11.8 15.7 18.3 5.9 6.9 15.6 22.1 17.8 35.6 2.2 13.4 2 43.2-1.1 53.1-1.2 3.9-1.4 8.7-1 13-1.7-2.8-2.9-4.4-3-4.6-.2-.3-.6-.5-.9-.6h-.5c-.2 0-.4.1-.5.2-.6.5-.8 1.4-.3 2 0 0 .2.3.5.8 1.4 2.1 5.6 8.4 8.9 16.7h-42.9v-43.8c0-.8-.6-1.4-1.4-1.4s-1.4.6-1.4 1.4v44.9c0 .1-.1.2-.1.3 0 .1 0 .2.1.3v9c-1.1 2-3.9 3.7-10.5 3.7h-7.5c-.4 0-.7.3-.7.7 0 .4.3.7.7.7h7.5c5 0 8.5-.9 10.5-2.8-.1 3.1-1.5 6.5-10.5 6.5H210.4c-9 0-10.5-3.4-10.5-6.5 2 1.9 5.5 2.8 10.5 2.8h67.4c.4 0 .7-.3.7-.7 0-.4-.3-.7-.7-.7h-67.4c-6.7 0-9.4-1.7-10.5-3.7v-54.5c0-.8-.6-1.4-1.4-1.4s-1.4.6-1.4 1.4v43.8h-43.6c4.2-10.2 9.4-17.4 9.5-17.5.5-.6.3-1.5-.3-2s-1.5-.3-2 .3c-.1.2-1.4 2-3.2 5 .1-4.9-.4-10.2-1.1-12.8zm221.4 60.2c-1.5 8.3-14.9 12-26.6 12H174.4c-11.8 0-25.1-3.8-26.6-12-1-5.7.6-19.3 4.6-30.2H197v9.8c0 6.4 4.5 9.7 13.4 9.7h105.4c8.9 0 13.4-3.3 13.4-9.7v-9.8h44c4 10.9 5.6 24.5 4.6 30.2z"></path><path d="M286.1 359.3c0 .4.3.7.7.7h14.7c.4 0 .7-.3.7-.7 0-.4-.3-.7-.7-.7h-14.7c-.3 0-.7.3-.7.7zm5.3-145.6c13.5-.5 24.7-2.3 33.5-5.3.4-.1.6-.5.4-.9-.1-.4-.5-.6-.9-.4-8.6 3-19.7 4.7-33 5.2-.4 0-.7.3-.7.7 0 .4.3.7.7.7zm-11.3.1c.4 0 .7-.3.7-.7 0-.4-.3-.7-.7-.7H242c-19.9 0-35.3-2.5-45.9-7.4-.4-.2-.8 0-.9.3-.2.4 0 .8.3.9 10.8 5 26.4 7.5 46.5 7.5h38.1zm-7.2 116.9c.4.1.9.1 1.4.1 1.7 0 3.4-.7 4.7-1.9 1.4-1.4 1.9-3.2 1.5-5-.2-.8-.9-1.2-1.7-1.1-.8.2-1.2.9-1.1 1.7.3 1.2-.4 2-.7 2.4-.9.9-2.2 1.3-3.4 1-.8-.2-1.5.3-1.7 1.1s.2 1.5 1 1.7z"></path><path d="M275.5 331.6c-.8 0-1.4.6-1.5 1.4 0 .8.6 1.4 1.4 1.5h.3c3.6 0 7-2.8 7.7-6.3.2-.8-.4-1.5-1.1-1.7-.8-.2-1.5.4-1.7 1.1-.4 2.3-2.8 4.2-5.1 4zm5.4 1.6c-.6.5-.6 1.4-.1 2 1.1 1.3 2.5 2.2 4.2 2.8.2.1.3.1.5.1.6 0 1.1-.3 1.3-.9.3-.7-.1-1.6-.8-1.8-1.2-.5-2.2-1.2-3-2.1-.6-.6-1.5-.6-2.1-.1zm-38.2 12.7c.5 0 .9 0 1.4-.1.8-.2 1.3-.9 1.1-1.7-.2-.8-.9-1.3-1.7-1.1-1.2.3-2.5-.1-3.4-1-.4-.4-1-1.2-.8-2.4.2-.8-.3-1.5-1.1-1.7-.8-.2-1.5.3-1.7 1.1-.4 1.8.1 3.7 1.5 5 1.2 1.2 2.9 1.9 4.7 1.9z"></path><path d="M241.2 349.6h.3c.8 0 1.4-.7 1.4-1.5s-.7-1.4-1.5-1.4c-2.3.1-4.6-1.7-5.1-4-.2-.8-.9-1.3-1.7-1.1-.8.2-1.3.9-1.1 1.7.7 3.5 4.1 6.3 7.7 6.3zm-9.7 3.6c.2 0 .3 0 .5-.1 1.6-.6 3-1.6 4.2-2.8.5-.6.5-1.5-.1-2s-1.5-.5-2 .1c-.8.9-1.8 1.6-3 2.1-.7.3-1.1 1.1-.8 1.8 0 .6.6.9 1.2.9z"></path></svg>`
                  }
                </a>
              </div>
              <div class="search_result_product_card_info">
                <p><a href="${p.url}">${p.title}</a></p>
                <div class="search_result_product_price">
                  <span>${Shopify.formatMoney(p.price_min)}</span>
                  ${p.compare_at_price_min > p.price_min
                ? `<s>${Shopify.formatMoney(p.compare_at_price_min)}</s>`
                : ""
              }
                </div>
              </div>
            </div>
          `,
          )
          .join(""),
      );
    }

    if (articles.length) {
      addGroup(
        "Articles",
        articles
          .map(
            (a) => `
            <div class="search_result_article_card">
            <div class="search_result_article_card_img ratio" style="--aspect-ratio: 100%;">
                <a href="${a.url}">
                  <img src="${a.image}" alt="${a.title}">
                </a>
              </div>
              <div class="search_result_product_card_info">
                <p><a href="${a.url}">${a.title}</a></p>
              </div>
            </div>
          `,
          )
          .join(""),
      );
    }

    if (pages.length) {
      addGroup(
        "Pages",
        pages.map((p) => `<p><a href="${p.url}">${p.title}</a></p>`).join(""),
      );
    }

    if (
      !queries.length &&
      !collections.length &&
      !products.length &&
      !articles.length &&
      !pages.length
    ) {
      this.resultsInner.innerHTML = `<h6 class="empty_results_heading">No results found.</h6>`;
    }
  }
}

customElements.define("search-drawer", SearchDrawer);

// LOCALIZATION
class LocalizationWrapper extends HTMLElement {
  constructor() {
    super();

    this.currencyForm = this.querySelector("#currency_selector_form");
    this.languageForm = this.querySelector("#language_selector_form");

    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
  }

  handleClick(e) {
    const button = e.target.closest("button[data-value]");
    if (!button) return;

    if (button.closest(".currency_list")) {
      this.submitForm(this.currencyForm, "country_code", button.dataset.value);
    }

    if (button.closest(".language_list")) {
      this.submitForm(this.languageForm, "language_code", button.dataset.value);
    }
  }

  submitForm(form, inputName, value) {
    if (!form) return;

    const input = form.querySelector(`input[name="${inputName}"]`);
    if (!input) return;

    input.value = value;
    form.submit();
  }
}

customElements.define("localization-wrapper", LocalizationWrapper);

// CUSTOMER LOGIN
class CustomerLogin extends HTMLElement {
  constructor() {
    super();

    this.loginForm = this.querySelector(".customer_login_form_wrapper");
    this.recoverForm = this.querySelector(".ustomer_recover_form_wrapper");
    this.forgotPassword = this.querySelector("#forgot_password");
    this.cancel = this.querySelector("#recover_cancel_link");
    this.showContent();
  }

  connectedCallback() {
    this.forgotPassword.addEventListener(
      "click",
      this.handleForgotPassword.bind(this),
    );
    this.cancel.addEventListener("click", this.handleCancel.bind(this));
  }

  showContent() {
    const url = window.location.href;
    if (url.includes("#recover")) {
      this.loginForm.classList.add("hidden");
      this.recoverForm.classList.remove("hidden");
    } else {
      this.loginForm.classList.remove("hidden");
      this.recoverForm.classList.add("hidden");
    }
  }

  handleForgotPassword(e) {
    e.preventDefault();
    this.loginForm.classList.add("hidden");
    this.recoverForm.classList.remove("hidden");
    const url = `${window.location.href}#recover`;
    window.history.replaceState({}, "", url);
  }

  handleCancel(e) {
    e.preventDefault();
    this.loginForm.classList.remove("hidden");
    this.recoverForm.classList.add("hidden");
    let url = window.location.href;
    url = url.split("#recover");
    window.history.replaceState({}, "", url[0]);
  }
}

customElements.define("customer-login", CustomerLogin);

// COLLECTION FILTER ICON
class CollectionFilterIcon extends HTMLElement {
  constructor() {
    super();
    this.filterWrapper = document.querySelector(".main_collection_filters");
    this.gridWrapper = document.querySelector(".main_collection_grid_wrapper");
  }

  connectedCallback() {
    this.closest("button").addEventListener(
      "click",
      this.handleClick.bind(this),
    );
  }

  handleClick() {
    if (!this.filterWrapper && this.gridWrapper) return;
    // this.filterWrapper.classList.toggle('active');
    const isActive = this.filterWrapper.classList.toggle("active");
    this.gridWrapper.classList.toggle("padding", isActive);

    if (isActive) {
      this.filterWrapper.removeAttribute("inert");
    } else {
      this.filterWrapper.setAttribute("inert", "");
    }
  }
}

customElements.define("collection-filter-icon", CollectionFilterIcon);

// COLLECTION FILTERS
class CollectionFilters extends HTMLElement {
  constructor() {
    super();

    this.inputs = this.querySelectorAll('input[type="checkbox"]');
    this.sectionId = this.dataset.sectionId;
    this.select = document.querySelector('select[name="sort_by"]');
    this.activeValues = this.querySelectorAll(".active_values_wrapper a");
    this.priceRanges = this.querySelectorAll('input[type="number"]');

    this.isLoading = false;
  }

  connectedCallback() {
    if (this.inputs.length) {
      this.inputs.forEach((input) => {
        input.addEventListener("change", (e) => {
          if (this.isLoading) return;

          this.isLoading = true;
          this.showLoaderSkeleton();
          this.disableFilters();

          const url = e.currentTarget.checked
            ? new URL(e.currentTarget.dataset.addUrl, window.location.origin)
            : new URL(
              e.currentTarget.dataset.removeUrl,
              window.location.origin,
            );

          url.searchParams.set("sections", this.sectionId);

          this.updatePage(url, this.sectionId);
          this.updateURL(url);
        });
      });
    }

    if (this.priceRanges.length) {
      this.priceRanges.forEach((priceRange) => {
        priceRange.addEventListener(
          "input",
          debounce(() => {
            if (this.isLoading) return;

            this.isLoading = true;
            this.showLoaderSkeleton();
            this.disableFilters();

            const minInput = this.querySelector(
              `input[name="${priceRange.name.includes("gte") ? priceRange.name : priceRange.name.replace("lte", "gte")}"]`,
            );
            const maxInput = this.querySelector(
              `input[name="${priceRange.name.includes("lte") ? priceRange.name : priceRange.name.replace("gte", "lte")}"]`,
            );

            const minVal = minInput ? minInput.value : "";
            const maxVal = maxInput ? maxInput.value : "";

            const url = new URL(window.location.href);

            if (minVal) {
              url.searchParams.set(minInput.name, minVal);
            } else {
              url.searchParams.delete(minInput?.name);
            }

            if (maxVal) {
              url.searchParams.set(maxInput.name, maxVal);
            } else {
              url.searchParams.delete(maxInput?.name);
            }

            url.searchParams.set("sections", this.sectionId);

            this.updatePage(url, this.sectionId);
            this.updateURL(url);
          }, 1000),
        );
      });
    }

    if (this.activeValues.length) {
      this.activeValues.forEach((activeValue) => {
        activeValue.addEventListener("click", (e) => {
          e.preventDefault();
          if (this.isLoading) return;

          this.isLoading = true;
          this.showLoaderSkeleton();
          this.disableFilters();

          const url = new URL(
            e.currentTarget.getAttribute("href"),
            window.location.origin,
          );

          url.searchParams.set("sections", this.sectionId);

          this.updatePage(url, this.sectionId);
          this.updateURL(url);
        });
      });
    }
  }

  showLoaderSkeleton() {
    const grid = document.querySelector(".main_collection_grid_wrapper");
    if (!grid) return;

    grid.classList.add("is-loading");
    grid.setAttribute("aria-busy", "true");
  }

  hideLoaderSkeleton() {
    const grid = document.querySelector(".main_collection_grid_wrapper");
    if (!grid) return;

    grid.classList.remove("is-loading");
    grid.removeAttribute("aria-busy");
  }

  disableFilters() {
    const filters = this.closest(".main_collection_filters");
    if (!filters) return;

    filters.setAttribute("inert", "");
    filters.classList.add("is-loading");
  }

  enableFilters() {
    const filters = this.closest(".main_collection_filters");
    if (!filters) return;

    filters.removeAttribute("inert");
    filters.classList.remove("is-loading");
  }

  updatePage(url, id) {
    const wasFilterOpen = this.closest(
      ".main_collection_filters",
    )?.classList.contains("active");

    fetch(url.toString())
      .then((res) => res.json())
      .then((htmlString) => {
        const temp = document.createElement("div");
        temp.innerHTML = htmlString[id];

        const newWrapper = temp.querySelector(".main_collection_inner_wrapper");
        const oldWrapper = document.querySelector(
          ".main_collection_inner_wrapper",
        );

        if (oldWrapper && newWrapper) {
          oldWrapper.innerHTML = newWrapper.innerHTML;

          if (wasFilterOpen) {
            const filterWrapper = document.querySelector(
              ".main_collection_filters",
            );
            const gridWrapper = document.querySelector(
              ".main_collection_grid_wrapper",
            );

            if (filterWrapper && gridWrapper) {
              filterWrapper.classList.add("active");
              filterWrapper.removeAttribute("inert");
              gridWrapper.classList.add("padding");
            }
          }
        }

        const newMobileDrawer = temp.querySelector('.mobile_filters_drawer_wrapper');
        const oldMobileDrawer = document.querySelector('.mobile_filters_drawer_wrapper');

        if (oldMobileDrawer && newMobileDrawer) {
          oldMobileDrawer.innerHTML = newMobileDrawer.innerHTML;
        }

        document.dispatchEvent(
          new CustomEvent("collection-grid-update", {
            detail: {
              url: url,
              sectionId: this.sectionId,
            },
          }),
        );
      })
      .catch((err) => {
        console.error("Error: ", err);
      })
      .finally(() => {
        this.hideLoaderSkeleton();
        this.enableFilters();
        this.isLoading = false;
      });
  }

  updateURL(url) {
    url.searchParams.delete("sections");
    window.history.replaceState({}, "", url.toString());
  }
}

customElements.define("collection-filters", CollectionFilters);


// COLLECTION SORTING
class CollectionSorting extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form#main_collection_sorting_form');
    this.select = this.querySelector('select[name="sort_by"]');
    this.sectionId = this.dataset.sectionId;

    this.isLoading = false;
  }

  connectedCallback() {
    this.select.addEventListener('change', () => {
      const value = this.select.value;

      this.isLoading = true;
      this.showLoaderSkeleton();
      this.disableFilters();

      const url = new URL(window.location.href, window.location.origin);
      url.searchParams.set('sort_by', value);
      url.searchParams.set('sections', this.sectionId);

      this.updatePage(url, this.sectionId);

      this.updateURL(url);
    })
  }

  showLoaderSkeleton() {
    const grid = document.querySelector(".main_collection_grid_wrapper");
    if (!grid) return;

    grid.classList.add("is-loading");
    grid.setAttribute("aria-busy", "true");
  }

  hideLoaderSkeleton() {
    const grid = document.querySelector(".main_collection_grid_wrapper");
    if (!grid) return;

    grid.classList.remove("is-loading");
    grid.removeAttribute("aria-busy");
  }

  disableFilters() {
    const filters = this.closest(".main_collection_filters");
    if (!filters) return;

    filters.setAttribute("inert", "");
    filters.classList.add("is-loading");
  }

  enableFilters() {
    const filters = this.closest(".main_collection_filters");
    if (!filters) return;

    filters.removeAttribute("inert");
    filters.classList.remove("is-loading");
  }

  updatePage(url, id) {
    const wasFilterOpen = this.closest(
      ".main_collection_filters",
    )?.classList.contains("active");

    fetch(url.toString())
      .then((res) => res.json())
      .then((htmlString) => {
        const temp = document.createElement("div");
        temp.innerHTML = htmlString[id];

        const newWrapper = temp.querySelector(".main_collection_inner_wrapper");
        const oldWrapper = document.querySelector(
          ".main_collection_inner_wrapper",
        );

        if (oldWrapper && newWrapper) {
          oldWrapper.innerHTML = newWrapper.innerHTML;

          if (wasFilterOpen) {
            const filterWrapper = document.querySelector(
              ".main_collection_filters",
            );
            const gridWrapper = document.querySelector(
              ".main_collection_grid_wrapper",
            );

            if (filterWrapper && gridWrapper) {
              filterWrapper.classList.add("active");
              filterWrapper.removeAttribute("inert");
              gridWrapper.classList.add("padding");
            }
          }
        }

        const newMobileDrawer = temp.querySelector('.mobile_filters_drawer_wrapper');
        const oldMobileDrawer = document.querySelector('.mobile_filters_drawer_wrapper');

        if (oldMobileDrawer && newMobileDrawer) {
          oldMobileDrawer.innerHTML = newMobileDrawer.innerHTML;
        }

        document.dispatchEvent(
          new CustomEvent("collection-grid-update", {
            detail: {
              url: url,
              sectionId: this.sectionId,
            },
          }),
        );
      })
      .catch((err) => {
        console.error("Error: ", err);
      })
      .finally(() => {
        this.hideLoaderSkeleton();
        this.enableFilters();
        this.isLoading = false;
      });
  }

  updateURL(url) {
    url.searchParams.delete("sections");
    window.history.replaceState({}, "", url.toString());
  }
}

customElements.define('collection-sorting', CollectionSorting);


// COLLECTION MOBILE FILTERS
class CollectionMobileFilters extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.sectionId;
    this.btn = this.querySelector(".collection_mobile_filters_button_wrapper button");
    this.form = this.querySelector("#mobile_filters_form");
    this.drawer = document.querySelector(".mobile_filters_drawer_wrapper");
    this.activeValues = this.querySelectorAll(".mobile_filters_drawer_active_values_wrapper a");

    this.isLoading = false;
  }

  connectedCallback() {
    this.attachListeners();
  }

  attachListeners() {

    /* APPLY BUTTON */
    if (this.btn) {
      this.btn.addEventListener("click", (e) => {
        e.preventDefault();

        if (!this.form || this.isLoading) return;

        this.isLoading = true;
        this.showLoaderSkeleton();
        this.disableFilters();

        this.submitForm();
      });
    }

    /* REMOVE ACTIVE FILTERS */
    if (this.activeValues.length) {
      this.activeValues.forEach((value) => {
        value.addEventListener("click", (e) => {
          e.preventDefault();

          if (this.isLoading) return;

          this.isLoading = true;
          this.showLoaderSkeleton();
          this.disableFilters();

          const url = new URL(
            e.currentTarget.getAttribute("href"),
            window.location.origin
          );

          url.searchParams.set("sections", this.sectionId);

          this.updatePage(url, this.sectionId);
          this.updateURL(url);
        });
      });
    }
  }

  showLoaderSkeleton() {
    const grid = document.querySelector(".main_collection_grid_wrapper");
    if (!grid) return;

    grid.classList.add("is-loading");
    grid.setAttribute("aria-busy", "true");
  }

  hideLoaderSkeleton() {
    const grid = document.querySelector(".main_collection_grid_wrapper");
    if (!grid) return;

    grid.classList.remove("is-loading");
    grid.removeAttribute("aria-busy");
  }

  disableFilters() {
    if (!this.drawer) return;

    this.drawer.setAttribute("inert", "");
    this.drawer.classList.add("is-loading");
  }

  enableFilters() {
    if (!this.drawer) return;

    this.drawer.removeAttribute("inert");
    this.drawer.classList.remove("is-loading");
  }

  submitForm() {

    const formData = new FormData(this.form);

    const priceGte = this.form.querySelector('[name="filter.v.price.gte"]');
    const priceLte = this.form.querySelector('[name="filter.v.price.lte"]');

    const defaultMin = priceGte?.getAttribute("data-default");
    const defaultMax = priceLte?.getAttribute("data-default");

    if (priceGte && priceLte && defaultMin && defaultMax) {
      if (priceGte.value === defaultMin && priceLte.value === defaultMax) {
        priceGte.value = "";
        priceLte.value = "";

        formData.delete("filter.v.price.gte");
        formData.delete("filter.v.price.lte");
      }
    }

    const params = new URLSearchParams();

    const currentParams = new URLSearchParams(window.location.search);
    const hasSortBy = formData.has("sort_by");

    for (const [key, value] of currentParams.entries()) {
      if (!key.startsWith("filter.v.") && !key.startsWith("filter.p.")) {
        if (key === "sort_by" && hasSortBy) continue;
        params.append(key, value);
      }
    }

    for (const [key, value] of formData.entries()) {
      if (value && value !== "") {
        if (key === "sort_by") {
          params.set(key, value);
        } else {
          params.append(key, value);
        }
      }
    }

    const url = new URL(`${window.location.pathname}?${params.toString()}`, window.location.origin);

    url.searchParams.set("sections", this.sectionId);

    this.updatePage(url, this.sectionId);
    this.updateURL(url);
  }

  updatePage(url, id) {

    fetch(url.toString())
      .then((res) => res.json())
      .then((htmlString) => {

        const temp = document.createElement("div");
        temp.innerHTML = htmlString[id];

        const newWrapper = temp.querySelector(".main_collection_inner_wrapper");
        const oldWrapper = document.querySelector(".main_collection_inner_wrapper");

        if (oldWrapper && newWrapper) {
          oldWrapper.innerHTML = newWrapper.innerHTML;
        }

        const newMobileDrawer = temp.querySelector(".mobile_filters_drawer_wrapper");
        const oldMobileDrawer = this.drawer;

        if (oldMobileDrawer && newMobileDrawer) {
          oldMobileDrawer.innerHTML = newMobileDrawer.innerHTML;
        }

        document.dispatchEvent(
          new CustomEvent("collection-grid-update", {
            detail: {
              url: url,
              sectionId: this.sectionId,
            },
          })
        );
      })
      .catch((err) => {
        console.error("Error:", err);
      })
      .finally(() => {

        this.hideLoaderSkeleton();
        this.enableFilters();
        this.isLoading = false;

        if (typeof closeDrawer === "function") {
          closeDrawer(this.drawer);
          RemoveScrollLock();
        }
      });
  }

  updateURL(url) {
    url.searchParams.delete("sections");
    window.history.replaceState({}, "", url.toString());
  }
}

customElements.define("collection-mobile-filters", CollectionMobileFilters);


document.addEventListener("collection-grid-update", () => {
  if (!scrollReveal) return;

  const wrapper = document.querySelector(".main_collection_inner_wrapper");
  if (!wrapper) return;

  // Re-observe newly injected cards
  scrollReveal.initElements(wrapper);
});


// INFINITE SCROLL
class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector("button");
    this.sectionId = this.dataset.sectionId;

    this.isLoading = false;
  }

  connectedCallback() {
    if (!this.button) return;

    this.button.addEventListener("click", () => {
      this.loadProducts();
    });
  }

  loadProducts() {
    const nextUrl = this.button.dataset.nextUrl;
    if (!nextUrl) return;

    if (this.isLoading) return;

    this.isLoading = true;
    this.showLoader();
    this.disableFilters();

    const url = new URL(nextUrl, window.location.origin);
    url.searchParams.set("sections", this.sectionId);

    this.updatePage(url, this.sectionId);
  }

  updatePage(url, id) {
    const wasFilterOpen = this.closest(
      ".main_collection_filters",
    )?.classList.contains("active");

    fetch(url.toString())
      .then((res) => res.json())
      .then((htmlString) => {
        const temp = document.createElement("div");
        temp.innerHTML = htmlString[id];

        const newWrapper = temp.querySelector(".main_collection_grid");
        const oldWrapper = document.querySelector(".main_collection_grid");

        if (oldWrapper && newWrapper) {
          const newItems = newWrapper.querySelectorAll(
            ".main_product_card_wrapper",
          );

          if (!newItems.length) {
            this.button.remove();
            return;
          }

          newItems.forEach((item) => {
            item.dataset.revealed = "false";
            oldWrapper.appendChild(item);
          });

          if (window.scrollReveal) {
            const revealItems = Array.from(newItems)
              .map((item) => item.querySelector(".reveal.stagger"))
              .filter(Boolean);

            scrollReveal.staggerBatch(revealItems);
          }

          if (wasFilterOpen) {
            const filterWrapper = document.querySelector(
              ".main_collection_filters",
            );
            const gridWrapper = document.querySelector(
              ".main_collection_grid_wrapper",
            );

            if (filterWrapper && gridWrapper) {
              filterWrapper.classList.add("active");
              filterWrapper.removeAttribute("inert");
              gridWrapper.classList.add("padding");
            }
          }

          const newButton = temp.querySelector("infinite-scroll button");
          if (newButton && newButton?.dataset.nextUrl) {
            this.button.dataset.nextUrl = newButton.dataset.nextUrl;
          } else {
            this.button.remove();
          }
        }

        document.dispatchEvent(
          new CustomEvent("collection-grid-update", {
            detail: {
              url: url,
              sectionId: this.sectionId,
            },
          }),
        );
      })
      .catch((err) => {
        console.error("Error: ", err);
      })
      .finally(() => {
        this.enableFilters();
        this.hideLoader();
        this.isLoading = false;
      });
  }

  showLoader() {
    const text = this.button.querySelector(".load_more");
    const loader = this.button.querySelector(".pagination_loader");

    if (!text && !loader) return;

    text.classList.add("hidden");
    loader.classList.remove("hidden");
  }

  hideLoader() {
    const text = this.button.querySelector(".load_more");
    const loader = this.button.querySelector(".pagination_loader");

    if (!text && !loader) return;

    text.classList.remove("hidden");
    loader.classList.add("hidden");
  }

  disableFilters() {
    const filters = this.closest(".main_collection_filters");
    if (!filters) return;

    filters.setAttribute("inert", "");
    filters.classList.add("is-loading");
  }

  enableFilters() {
    const filters = this.closest(".main_collection_filters");
    if (!filters) return;

    filters.removeAttribute("inert");
    filters.classList.remove("is-loading");
  }
}

customElements.define("infinite-scroll", InfiniteScroll);


// QUICK ADD BUTTON
class QuickAddButton extends HTMLElement {
  constructor() {
    super();
    this.btn = this.querySelector(".quick_add_btn");
    this.productHandle = this.dataset.productHandle;
  }

  connectedCallback() {
    if (!this.btn) return;
    this.btn.addEventListener("click", this.handleClick.bind(this));
  }

  async handleClick() {
    const drawer = document.querySelector(".quick_add_drawer_wrapper");
    if (!drawer || !this.btn) return;

    const skeleton = drawer.querySelector(".qv-skeleton");
    const content = drawer.querySelector(".quick_view_content");

    content.innerHTML = "";
    skeleton.classList.remove("hidden");

    openDrawer(drawer, this.btn);
    AddScrollLock();

    const fetchPromise = fetch(
      `${Shopify.routes.root}products/${this.productHandle}?section_id=quick-add-drawer`,
    ).then((r) => r.text());

    const delayPromise = new Promise((resolve) => setTimeout(resolve, 1000));

    const [html] = await Promise.all([fetchPromise, delayPromise]);

    const doc = new DOMParser().parseFromString(html, "text/html");
    const newContent = doc.querySelector(".quick_view_content");

    if (!newContent) return;

    skeleton.classList.add("hidden");
    content.innerHTML = newContent.innerHTML;
  }
}

customElements.define("quick-add-button", QuickAddButton);

// MAIN BLOG
class MainBlog extends HTMLElement {
  constructor() {
    super();
    this.blogHandle = this.dataset.blog;
    this.isFetching = false;
    this.inner = this.querySelector('.main_blog_inner');
    this.initComponent();
  }

  handleGrid() {
    this.cards.forEach((card, index) => {
      const position = index % 5;

      card.classList.remove("two_cols", "three_cols");

      if (position < 3) {
        card.classList.add("three_cols");
      } else {
        card.classList.add("two_cols");
      }
    });
  }

  bindTagEvents() {
    this.tags?.forEach((tag) => {
      tag.addEventListener("click", (e) => {
        e.preventDefault();
        if (this.isFetching) return;
        this.querySelector(".main_blog_inner")?.classList.add("is-loading");
        const tagParent = tag.closest(".main_blog_tags");
        this.handleTagsClick(tagParent, tag.dataset.tag);
      });
    });
  }

  updateCountText(newCount, newCountAll, useAllCount = false) {
    const countWrapper = this.previousElementSibling?.querySelector(
      ".main_blog_count span",
    );

    const countToUse = useAllCount ? newCountAll : newCount;

    if (countWrapper && countToUse !== undefined) {
      countWrapper.textContent =
        countToUse == "1"
          ? `${countToUse} article found`
          : `${countToUse} articles found`;
    }
  }

  handleTagsClick(t = null, tag) {
    if (this.isFetching) return;
    this.isFetching = true;

    document.querySelectorAll(".main_blog_tags").forEach((main_blog_tag) => {
      main_blog_tag.classList.remove("active");
    });
    t.classList.add("active");

    const isAll = tag === "all";
    const url = isAll
      ? `/blogs/${this.blogHandle}`
      : `/blogs/${this.blogHandle}/tagged/${tag}`;

    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const newInner = doc.querySelector(".main_blog_wrapper");
        const currentInner = this.querySelector(".main_blog_wrapper");

        if (newInner && currentInner) {
          currentInner.replaceWith(newInner);
        }

        // reinitialize component
        this.initComponent(tag);
        scrollReveal.initElements(this);
      })
      .catch((err) => console.error("Failed to load filtered blog:", err))
      .finally(() => {
        this.querySelector(".main_blog_inner")?.classList.remove("is-loading");
        this.isFetching = false;
      });
  }

  initComponent(activeTag = "all") {
    this.cards = [...this.querySelectorAll(".main_blog_article_wrapper")];
    this.tags = [
      ...(this.querySelectorAll(".main_blog_tags a") ||
        []),
    ];
    this.count = this.querySelector(".main_blog_inner")?.dataset.count;

    this.handleGrid();
    this.bindTagEvents();

    this.setActiveTag(activeTag);
  }

  setActiveTag(tag) {
    const tagWrappers = this.querySelectorAll(".main_blog_tags");

    tagWrappers.forEach((wrapper) => {
      wrapper.classList.remove("active");

      const link = wrapper.querySelector("a");

      if (link && link.dataset.tag === tag) {
        wrapper.classList.add("active");
      }
    });
  }
}

customElements.define("main-blog", MainBlog);

// VIDEO IMAGE ALT
document.addEventListener("DOMContentLoaded", () => {
  const vdos = document.querySelectorAll("video");
  if (vdos.length) {
    vdos.forEach((vdo) => {
      const img = vdo.querySelector("img");
      if (!img) return;
      img.setAttribute("alt", "Video poster image");
    });
  }
});

// CART QUANTITY SELECTOR
class CartQuantitySelector extends HTMLElement {
  constructor() {
    super();

    this.btnMinus = this.querySelector("[data-minus]");
    this.input = this.querySelector('input[type="text"]');
    this.btnPlus = this.querySelector("[data-plus]");
    this.inputValue = Number(this.input.value);
    this.step = Number(this.input.getAttribute("step"));
    this.min = this.input.dataset.min;
    this.max = this.input.dataset.max;
    this.line = this.dataset.line;
  }

  connectedCallback() {
    this.btnMinus.addEventListener("click", this.handleChange.bind(this));
    this.btnPlus.addEventListener("click", this.handleChange.bind(this));
    const debouncedInputandler = debounce((e) => this.handleInput(e), 1000);
    this.input.addEventListener("input", debouncedInputandler);
  }

  handleChange(e) {
    if (e.currentTarget.hasAttribute("data-plus")) {
      this.inputValue = this.inputValue + this.step;
      this.input.value = this.inputValue;
    } else {
      this.inputValue = this.inputValue - this.step;
      this.input.value = this.inputValue;
    }

    if (this.min) {
      if (this.inputValue <= this.min) {
        this.btnMinus.setAttribute("disabled", "");
      } else {
        this.btnMinus.removeAttribute("disabled");
      }
    }

    if (this.max) {
      if (this.inputValue >= this.max) {
        this.btnMinus.setAttribute("disabled", "");
      } else {
        this.btnPlus.removeAttribute("disabled");
      }
    }

    document.dispatchEvent(
      new CustomEvent("cart-quantity-change", {
        detail: {
          value: this.inputValue,
          line: this.line,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  handleInput(e) {
    if (this.input.value === '') return;

    this.value = Number(this.input.value);
    if (this.value == 0) {
      this.value = 1;
      this.input.value = 1;
    }

    if (this.min) {
      if (this.value <= this.min) {
        this.btnMinus.setAttribute("disabled", "");
      } else {
        this.btnMinus.removeAttribute("disabled");
      }
    }

    if (this.max) {
      if (this.value >= this.max) {
        this.btnMinus.setAttribute("disabled", "");
      } else {
        this.btnMinus.removeAttribute("disabled");
      }
    }

    this.dispatchEvent(
      new CustomEvent("cart-quantity-change", {
        detail: {
          value: this.value,
          line: this.line,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

customElements.define("cart-quantity-selector", CartQuantitySelector);

// CART ITEM REMOVE
class CartItemRemove extends HTMLElement {
  constructor() {
    super();
    this.line = this.dataset.line;
    this.removeBtn = this.querySelector(".item_remove_btn");
  }

  connectedCallback() {
    this.removeBtn.addEventListener("click", this.handleClick.bind(this));
  }

  handleClick(e) {
    e.preventDefault();

    document.dispatchEvent(
      new CustomEvent("cart-item-remove", {
        detail: {
          value: 0,
          line: this.line,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

customElements.define("cart-item-remove", CartItemRemove);

// CART NOTE
class CartNoteDiscount extends HTMLElement {
  constructor() {
    super();
    this.cartNoteBtn = this.querySelector(".cart_note_head");
    this.discountBtn = this.querySelector(".cart_discount_head");
    this.textarea = this.querySelector("textarea");
    this.noteWrapper = this.querySelector(".cart_note_wrapper");
    this.discountWrapper = this.querySelector(
      ".cart_drawer_discount_input_wrapper",
    );
    this.discountInput = this.querySelector('input[name="discount"]');
    this.value = this.discountInput.value;
    this.btn = this.querySelector('button[name="cart_discount_btn"]');
    this.remove = this.querySelector("[data-cart-discount-remove]");
    this.error = this.querySelector(".cart_discount_error");

    this.debounceTimer = null;
  }

  connectedCallback() {
    if (this.cartNoteBtn) {
      this.cartNoteBtn.addEventListener(
        "click",
        this.handleNoteClick.bind(this),
      );
    }

    if (this.discountBtn) {
      this.discountBtn.addEventListener(
        "click",
        this.handleDiscountClick.bind(this),
      );
    }

    this.textarea?.addEventListener("input", () => {
      clearTimeout(this.debounceTimer);

      this.debounceTimer = setTimeout(() => {
        this.updateCartNote(this.textarea.value.trim());
      }, 500);
    });

    this.setInitialState();
    this.setHeights();

    this.discountInput.addEventListener("input", this.handleInput.bind(this));
    this.btn.addEventListener("click", this.handleClick.bind(this));
    if (!this.remove) return;
    this.remove.addEventListener("click", this.handleRemove.bind(this));
  }

  handleInput() {
    if (this.error) {
      this.error.classList.add("hidden");

      requestAnimationFrame(() => {
        this.recalculateDiscountHeight();
      });
    }

    this.value = this.discountInput.value.trim();
  }

  handleClick(e) {
    e.preventDefault();

    if (!this.value) {
      if (this.error) {
        this.error.classList.remove("hidden");

        // ⬇️ Recalculate AFTER error becomes visible
        requestAnimationFrame(() => {
          this.recalculateDiscountHeight();
        });

        this.discountInput.focus();
        return;
      }
    }

    document.dispatchEvent(
      new CustomEvent("discount-button-click", {
        detail: {
          discount: this.value,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  recalculateDiscountHeight() {
    if (!this.discountWrapper) return;

    const inner = this.discountWrapper.querySelector(
      ".cart_drawer_discount_input_inner",
    );

    if (!inner) return;

    this.discountWrapper.style.setProperty(
      "--height",
      `${inner.offsetHeight}px`,
    );
  }

  handleRemove(e) {
    e.preventDefault();

    document.dispatchEvent(
      new CustomEvent("discount-remove-click", {
        detail: {
          discount: "",
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  handleNoteClick() {
    const isOpen = this.noteWrapper.hasAttribute("open");

    if (isOpen) {
      this.closeNote();
    } else {
      this.closeDiscount();
      this.openNote();
    }
  }

  openNote() {
    this.noteWrapper.setAttribute("open", "");
    this.textarea.removeAttribute("inert");
    this.textarea.setAttribute("aria-hidden", "false");
    // this.textarea.focus();
  }

  closeNote() {
    this.noteWrapper.removeAttribute("open");
    this.textarea.setAttribute("inert", "");
    this.textarea.setAttribute("aria-hidden", "true");
  }

  handleDiscountClick() {
    const isOpen = this.discountWrapper.hasAttribute("open");

    if (isOpen) {
      this.closeDiscount();
    } else {
      this.closeNote();
      this.openDiscount();
    }
  }

  openDiscount() {
    this.discountWrapper.setAttribute("open", "");
    this.discountWrapper
      .querySelectorAll("input, button")
      .forEach((el) => el.removeAttribute("inert"));
  }

  closeDiscount() {
    this.discountWrapper.removeAttribute("open");
    this.discountWrapper
      .querySelectorAll("input, button")
      .forEach((el) => el.setAttribute("inert", ""));
  }

  setInitialState() {
    if (this.textarea?.value.trim().length) {
      this.openNote();
    }
  }

  setHeights() {
    if (this.noteWrapper) {
      const inner = this.noteWrapper.querySelector(".cart_note_inner");
      this.noteWrapper.style.setProperty(
        "--height",
        `${inner?.offsetHeight || 0}px`,
      );
    }

    if (this.discountWrapper) {
      const inner = this.discountWrapper.querySelector(
        ".cart_drawer_discount_input_inner",
      );
      this.discountWrapper.style.setProperty(
        "--height",
        `${inner?.offsetHeight || 0}px`,
      );
    }
  }

  updateCartNote(value) {
    fetch(`${window.Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: value }),
    }).catch((err) => {
      console.error("Error updating cart note:", err);
    });
  }
}

customElements.define("cart-note-discount", CartNoteDiscount);


// CART CONTROLLER
class CartController {
  static init() {
    this.locked = false;

    document.addEventListener("cart-quantity-change", (e) =>
      this.updateLine(e.detail.line, e.detail.value)
    );

    document.addEventListener("cart-item-remove", (e) =>
      this.updateLine(e.detail.line, 0)
    );

    document.addEventListener("discount-button-click", (e) =>
      this.updateDiscount(e.detail.discount)
    );

    document.addEventListener("discount-remove-click", (e) =>
      this.updateDiscount("")
    );

    document.addEventListener("cart-note-change", (e) =>
      this.updateNote(e.detail.note)
    );
  }

  static startLoading() {
    document.dispatchEvent(
      new CustomEvent("cart-loading", {
        detail: { loading: true },
        bubbles: true,
      })
    );
  }

  static stopLoading() {
    document.dispatchEvent(
      new CustomEvent("cart-loading", {
        detail: { loading: false },
        bubbles: true,
      })
    );
  }

  static async updateLine(line, quantity) {
    if (this.locked) return;
    this.locked = true;
    this.startLoading();

    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          line,
          quantity,
          sections: "cart-drawer,main-cart,cart-bubble",
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const sections = await fetch(
          `${window.Shopify.routes.root}?sections=cart-drawer,main-cart,cart-bubble`
        ).then((r) => r.json());

        document.dispatchEvent(
          new CustomEvent("cart-update", {
            detail: {
              line,
              message: data.message || data.description,
              sections,
            },
            bubbles: true,
          })
        );

        return;
      }

      this.dispatchUpdate(data);
    } catch (err) {
      console.error("Cart line update failed:", err);
    } finally {
      this.locked = false;
      this.stopLoading();
    }
  }

  static async updateDiscount(discount) {
    if (this.locked) return;
    this.locked = true;
    this.startLoading();

    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          discount,
          sections: "cart-drawer,main-cart,cart-bubble",
        }),
      });

      const data = await res.json();

      const discountData = data.discount_codes?.[0];

      if (discount && discountData && discountData.applicable === false) {
        document.dispatchEvent(
          new CustomEvent("cart-discount-error", {
            detail: {
              message: "Invalid or expired discount code",
            },
            bubbles: true,
          })
        );
        return;
      }

      this.dispatchUpdate(data);
    } catch (err) {
      console.error(err);
    } finally {
      this.locked = false;
      this.stopLoading();
    }
  }

  static async updateNote(note) {
    if (this.locked) return;
    this.locked = true;
    this.startLoading();

    try {
      const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          note,
          sections: "cart-drawer,main-cart",
        }),
      });

      const data = await res.json();
      this.dispatchUpdate(data);
    } catch (err) {
      console.error(err);
    } finally {
      this.locked = false;
      this.stopLoading();
    }
  }

  static dispatchUpdate(data) {
    document.dispatchEvent(
      new CustomEvent("cart-update", {
        detail: data,
        bubbles: true,
      })
    );
  }
}

CartController.init();

// MAIN CART
class MainCart extends HTMLElement {
  constructor() {
    super();
    this.inner = this.querySelector(".main_cart_main_inner");

    this.handleUpdate = updateCart.bind(this);
    this.handleLoading = this.handleLoading.bind(this);
    this.handleDiscountError = this.handleDiscountError.bind(this);
  }

  connectedCallback() {
    document.addEventListener("cart-update", this.handleUpdate);
    document.addEventListener("cart-loading", this.handleLoading);
    document.addEventListener("cart-discount-error", this.handleDiscountError);

    this.querySelector('input[name="discount"]')
      ?.addEventListener("input", () => {
        this.querySelector(".cart_discount_error")?.classList.add("hidden");
      });
  }

  disconnectedCallback() {
    document.removeEventListener("cart-update", this.handleUpdate);
    document.removeEventListener("cart-loading", this.handleLoading);
    document.removeEventListener("cart-discount-error", this.handleDiscountError);
  }

  handleLoading(e) {
    if (!this.inner) return;
    this.inner.classList.toggle("loading", e.detail.loading);
  }

  handleDiscountError(e) {
    const error = this.querySelector(".cart_discount_error");
    const input = this.querySelector('input[name="discount"]');

    if (!error) return;

    error.textContent = e.detail.message;
    error.classList.remove("hidden");

    if (input) input.value = "";

    requestAnimationFrame(() => {
      document
        .querySelectorAll(".cart_drawer_discount_input_inner")
        .forEach((el) => {
          el.closest(".cart_drawer_discount_input_wrapper")?.style.setProperty(
            "--height",
            `${el.offsetHeight}px`
          );
        });
    });
  }
}

customElements.define("main-cart", MainCart);


// CART DRAWER
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.inner = this.querySelector(".cart_drawer_inner");

    this.handleUpdate = updateCart.bind(this);
    this.handleLoading = this.handleLoading.bind(this);
    this.handleDiscountError = this.handleDiscountError.bind(this);
  }

  connectedCallback() {
    document.addEventListener("cart-update", this.handleUpdate);
    document.addEventListener("cart-loading", this.handleLoading);
    document.addEventListener("cart-discount-error", this.handleDiscountError);
    this.querySelector('input[name="discount"]')
      ?.addEventListener("input", () => {
        this.querySelector(".cart_discount_error")?.classList.add("hidden");
      });
  }

  disconnectedCallback() {
    document.removeEventListener("cart-update", this.handleUpdate);
    document.removeEventListener("cart-loading", this.handleLoading);
    document.removeEventListener("cart-discount-error", this.handleDiscountError);
  }

  handleLoading(e) {
    if (!this.inner) return;
    this.inner.classList.toggle("loading", e.detail.loading);
  }

  handleDiscountError(e) {
    const error = this.querySelector(".cart_discount_error");
    const input = this.querySelector('input[name="discount"]');

    if (!error) return;

    error.textContent = e.detail.message;
    error.classList.remove("hidden");

    if (input) input.value = "";

    requestAnimationFrame(() => {
      document
        .querySelectorAll(".cart_drawer_discount_input_inner")
        .forEach((el) => {
          el.closest(".cart_drawer_discount_input_wrapper")?.style.setProperty(
            "--height",
            `${el.offsetHeight}px`
          );
        });
    });
  }
}

customElements.define("cart-drawer", CartDrawer);

// UPDATE CART
function updateCart(e) {
  const data = e.detail;
  const updatedDrawer = data.sections["cart-drawer"];
  const updatedCartPage = data.sections["main-cart"];
  const updatedCartBubble = data.sections["cart-bubble"];
  const div = document.createElement("div");
  const pageDiv = document.createElement("div");
  const bubbleDiv = document.createElement("div");
  const errorMsg = e.detail.message;
  const line = e.detail.line;

  // CART DRAWER UPDATE
  if (updatedDrawer) {
    div.innerHTML = updatedDrawer;
  }

  const oldDrawer = document.querySelector('cart-drawer');

  if (oldDrawer) {
    const drawerHeader = oldDrawer.querySelector(
      ".cart_drawer_header .dh_count",
    );
    const drawerContent = oldDrawer.querySelector(".cart_drawer_contents");
    const drawerFooter = oldDrawer.querySelector(".cart_drawer_footer_wrapper");

    if (drawerHeader) {
      drawerHeader.innerHTML = div.querySelector(
        ".cart_drawer_header .dh_count",
      ).innerHTML;
    }

    if (drawerContent) {
      drawerContent.innerHTML = div.querySelector(
        ".cart_drawer_contents",
      ).innerHTML;
      if (errorMsg && line) {
        const errorWrapper = drawerContent.querySelector(
          `.cart_drawer_error[data-line="${line}"]`,
        );

        if (errorWrapper) {
          errorWrapper.innerHTML = `<p>${errorMsg}</p>`;
          errorWrapper.removeAttribute("hidden");
        }
      }
    }

    if (drawerFooter) {
      const updatedDrawerFooter = div.querySelector(
        ".cart_drawer_footer_wrapper",
      );
      if (updatedDrawerFooter) {
        drawerFooter.innerHTML = div.querySelector(
          ".cart_drawer_footer_wrapper",
        ).innerHTML;
    }
    }
  }

  // MAIN CART PAGE UPDATE
  if (updatedCartPage) {
    pageDiv.innerHTML = updatedCartPage;
  }

  const oldCartPage = document.querySelector('.main_cart_main_inner');
  const newCartPage = pageDiv.querySelector('.main_cart_main_inner');

  if (oldCartPage && newCartPage) {    
    oldCartPage.innerHTML = newCartPage.innerHTML;

    if(newCartPage.classList.contains('empty_cart')) {
      oldCartPage.classList.add('empty_cart');
    }

    scrollReveal.initElements(oldCartPage);

    if (errorMsg && line) {
      const errorWrapper = oldCartPage.querySelector(`.cart_error[data-line="${line}"]`);

      if (errorWrapper) {
        errorWrapper.innerHTML = `<p>${errorMsg}</p>`
        errorWrapper.removeAttribute('hidden')
      }
    }

  }

  // CART BUBBLE UPDATE
  if (updatedCartBubble) {
    bubbleDiv.innerHTML = updatedCartBubble;
  }

  const oldBubble = document.querySelector(".cart_count_text");
  if (oldBubble) {
    oldBubble.innerHTML = bubbleDiv.querySelector(".cart_count_text").innerHTML;
  }

  // AFTER DOM updates
  requestAnimationFrame(() => {
    document
      .querySelectorAll(".cart_drawer_discount_input_inner")
      .forEach((el) => {
        el.closest(".cart_drawer_discount_input_wrapper")?.style.setProperty(
          "--height",
          `${el.offsetHeight}px`
        );
      });
  });
}

// QUANTITY SELECTOR
class QuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.onButtonClick = this.onButtonClick.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
    this.onInputCommit = this.onInputCommit.bind(this);
  }

  connectedCallback() {
    this.btnMinus = this.querySelector("[data-minus]");
    this.btnPlus = this.querySelector("[data-plus]");
    this.input = this.querySelector('input[type="text"]');

    this.step = Number(this.input.step) || 1;
    this.min = Number(this.input.dataset.min) || 1;
    this.max = Number(this.input.dataset.max) || null;

    this.btnMinus.addEventListener("click", this.onButtonClick);
    this.btnPlus.addEventListener("click", this.onButtonClick);
    this.input.addEventListener("input", this.onInputChange);
    this.input.addEventListener("change", this.onInputCommit);

    this.updateButtons();
  }

  get value() {
    return Number(this.input.value) || this.min;
  }

  set value(val) {
    const clamped = this.max
      ? Math.min(Math.max(val, this.min), this.max)
      : Math.max(val, this.min);

    this.input.value = clamped;
    this.updateButtons();
    this.emitChange(clamped);
  }

  onButtonClick(e) {
    if (e.currentTarget === this.btnPlus) {
      this.value = this.value + this.step;
    } else {
      this.value = this.value - this.step;
    }
  }

  onInputChange() {
    // Update button states while typing without overwriting the field,
    // so the user can freely clear and re-enter a value.
    this.updateButtons();
  }

  onInputCommit() {
    // Clamp and emit once the user leaves the field (blur / Enter).
    this.value = this.value;
  }

  updateButtons() {
    this.btnMinus.disabled = this.value <= this.min;
    this.btnPlus.disabled = this.max && this.value >= this.max;
  }

  emitChange(value) {
    this.dispatchEvent(
      new CustomEvent("quantity-change", {
        detail: { value },
        bubbles: true,
        composed: true,
      }),
    );
  }
}

customElements.define("quantity-selector", QuantitySelector);

// VARIANT SELECTOR
class VariantSelector extends HTMLElement {
  connectedCallback() {
    this.addEventListener("change", this.onChange);
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.onChange);
  }

  onChange = () => {
    const selectedOptions = this.selectedOptions;

    this.dispatchEvent(
      new CustomEvent("variant-change", {
        detail: { selectedOptions },
        bubbles: true,
        composed: true,
      }),
    );
  };

  get selectedOptions() {
    const selectValues = Array.from(this.querySelectorAll("select")).map(
      (select) => select.selectedOptions[0]?.dataset.value,
    );

    const radioValues = Array.from(
      this.querySelectorAll('input[type="radio"]:checked'),
    ).map((radio) => radio.dataset.value);

    return [...selectValues, ...radioValues].filter(Boolean);
  }
}

customElements.define("variant-selector", VariantSelector);

// PRODUCT FORM
class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.handleSubmit = this.handleSubmit.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.isSubmitting = false;
  }

  connectedCallback() {
    this.cacheElements();

    this.addEventListener("submit", this.handleSubmit);
    this.addEventListener("variant-change", this.onVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener("submit", this.handleSubmit);
    this.removeEventListener("variant-change", this.onVariantChange);
  }

  cacheElements() {
    this.form = this.querySelector("form");
    this.button = this.querySelector(".pf_submit_btn");
    this.btnText = this.button?.querySelector(".btn_text");
    this.loader = this.button?.querySelector(".loader");
    this.cart = document.querySelector("cart-drawer");
    this.openDrawer = false;
  }

  onVariantChange() {
    this.cacheElements();
  }

  async handleSubmit(e) {
    if (!e.target.closest("form")) return;

    e.preventDefault();
    e.stopPropagation();

    if (this.isSubmitting) return;
    this.isSubmitting = true;

    this.cacheElements();
    if (!this.form || !this.button) {
      this.isSubmitting = false;
      return;
    }

    this.button.setAttribute("disabled", "disabled");
    this.button.setAttribute("aria-busy", "true");

    if (this.btnText) this.btnText.classList.add("hidden");

    if (this.loader) this.loader.classList.remove("hidden");

    try {
      const formData = new FormData(this.form);

      const res = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { "X-Requested-With": "XMLHttpRequest" },
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) {
        this.showError(data);
        const sections = await fetch(
          `${Shopify.routes.root}?sections=cart-drawer,main-cart,cart-bubble`,
        ).then((r) => r.json());

        document.dispatchEvent(
          new CustomEvent("cart-update", {
            detail: { sections },
            bubbles: true,
          }),
        );
        throw data;
      }

      if (this.cart) {
        const sections = await fetch(
          `${Shopify.routes.root}?sections=cart-drawer,main-cart,cart-bubble`,
        ).then((r) => r.json());

        document.dispatchEvent(
          new CustomEvent("cart-update", {
            detail: { sections },
            bubbles: true,
          }),
        );

        this.openDrawer = true;
      } else {
        window.location.href = `${Shopify.routes.root}cart`;
      }
    } catch (err) {
      this.showError(err);
    } finally {
      this.isSubmitting = false;

      if (this.button) {
        this.button.removeAttribute("disabled");
        this.button.removeAttribute("aria-busy");
      }
      if (this.btnText) this.btnText.classList.remove("hidden");

      if (this.loader) this.loader.classList.add("hidden");

      if (this.openDrawer && this.cart) {
        const drawer = this.cart.querySelector(".cart_drawer_wrapper");
        openDrawer(drawer, this.button);
        AddScrollLock();
      }
    }
  }

  showError(data) {
    const error =
      this.querySelector(".product_add_error") ||
      this.closest(".main_product_card_wrapper").querySelector(
        ".product_add_error",
      );
    if (!error) return;

    let message = "Unable to add product to cart";

    if (data?.errors && typeof data.errors === "object") {
      const formattedErrors = [];

      Object.entries(data.errors).forEach(([field, value]) => {
        let fieldLabel = "";

        if (field === "email") fieldLabel = "Email";

        if (value === "can't be blank") {
          formattedErrors.push(`${fieldLabel} can't be blank`);
        } else if (value === "is invalid") {
          formattedErrors.push(`${fieldLabel} is invalid`);
        } else {
          formattedErrors.push(`${fieldLabel} ${value}`);
        }
      });

      message = formattedErrors.join("<br>");
    } else if (typeof data?.errors === "string") {
      message = data.errors;
    } else if (typeof data?.description === "string") {
      message = data.description;
    } else if (typeof data?.message === "string") {
      message = data.message;
    }

    error.innerHTML = message;
    error.classList.remove("hidden");
  }
}

customElements.define("product-form", ProductForm);

// QUICK VIEW PRODUCT
class QuickViewProduct extends HTMLElement {
  constructor() {
    super();
    this.abortController = null;
    this.cache = new Map();

    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.productUrl = this.dataset.productUrl;
    this.drawer = document.querySelector(".quick_add_drawer_wrapper");
  }

  connectedCallback() {
    this.addEventListener("quantity-change", this.onQuantityChange);
    this.addEventListener("variant-change", this.onVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener("quantity-change", this.onQuantityChange);
    this.removeEventListener("variant-change", this.onVariantChange);
  }

  setLoading(state) {
    if (!this.drawer) return;
    const inner = this.drawer.querySelector(".quick_add_drawer_inner");
    if (inner) {
      inner.classList.toggle("loading", state);
    }
  }

  onQuantityChange(e) {
    const { value } = e.detail;
    const input = this.querySelector('input[name="quantity"]');
    if (input) {
      input.value = value;
    }
  }

  onVariantChange(e) {
    if (!e.detail?.selectedOptions) return;

    this.setLoading(true);

    this.requestedURL = this.buildURL(e.detail.selectedOptions);

    this.loadProductInfo(this.requestedURL);
  }

  buildURL(options) {
    const params = new URLSearchParams();
    if (options.length) {
      params.append("option_values", options.join(","));
    }

    params.append("section_id", "quick-add-drawer");

    return `${this.productUrl}?${params.toString()}`;
  }

  loadProductInfo(url) {
    if (this.abortController) {
      this.abortController.abort();
    }

    if (this.cache.has(url)) {
      const html = this.cache.get(url);
      const doc = new DOMParser().parseFromString(html, "text/html");
      this.updateQuickDrawer(doc);
      this.setLoading(false);
      return;
    }

    this.abortController = new AbortController();
    
    fetch(url, { signal: this.abortController.signal })
      .then((res) => res.text())
      .then((html) => {
        this.cache.set(url, html);

        const doc = new DOMParser().parseFromString(html, "text/html");
        this.updateQuickDrawer(doc);
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error("Error loading product info:", err);
        }
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  updateQuickDrawer(doc) {
    let container = doc.querySelector("#shopify-section-quick-add-drawer") || doc.body.firstElementChild;


    if (!container) return;

    // UPDATE IMAGES
    const oldImages = this.querySelector(".quick_view_product_images");
    const updatedImages = container.querySelector(".quick_view_product_images");

    if (oldImages && updatedImages) {
      oldImages.innerHTML = updatedImages.innerHTML;
    }

    // UPDATE PRICE
    const oldPrice = this.querySelector(".quick_add_product_price");
    const updatedPrice = container.querySelector(".quick_add_product_price");

    if (oldPrice && updatedPrice) {
      oldPrice.innerHTML = updatedPrice.innerHTML;
    }

    // UPDATE INVENTORY
    const oldInventory = this.querySelector(".quick_add_inventory_wrapper");
    const updatedInventory = container.querySelector(
      ".quick_add_inventory_wrapper",
    );

    if (oldInventory && updatedInventory) {
      oldInventory.innerHTML = updatedInventory.innerHTML;
    }

    // UPDATE VARIANT SELECTOR
    const oldVariantSelector = this.querySelector(
      ".quick_add_variant_picker_wrapper",
    );
    const updatedVariantSelector = container.querySelector(
      ".quick_add_variant_picker_wrapper",
    );

    if (oldVariantSelector && updatedVariantSelector) {
      oldVariantSelector.innerHTML = updatedVariantSelector.innerHTML;
    }

    // UPDATE QUANTITY
    const oldQuantity = this.querySelector(".quick_add_quantity_wrapper");
    const updatedQuantity = container.querySelector(
      ".quick_add_quantity_wrapper",
    );
    const oldMainQty = this.querySelector('input[name="quantity"]');
    const updatedMainQty = container.querySelector('input[name="quantity"]');

    if (oldQuantity && updatedQuantity) {
      oldQuantity.innerHTML = updatedQuantity.innerHTML;
    }

    // // UPDATE ACTION BUTTONS
    const oldActionBtns = this.querySelector(
      ".quick_add_action_buttons_wrapper",
    );
    const updatedActionBtns = container.querySelector(
      ".quick_add_action_buttons_wrapper",
    );

    if (oldActionBtns && updatedActionBtns) {
      oldActionBtns.innerHTML = updatedActionBtns.innerHTML;
    }
  }
}

customElements.define("quick-view-product", QuickViewProduct);


// MAIN PRODUCT
class MainProduct extends HTMLElement {
  constructor() {
    super();
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.productUrl = this.dataset.productUrl;
    this.updateUrl = this.dataset.updateUrl === 'true';
  }

  connectedCallback() {
    this.addEventListener("quantity-change", this.onQuantityChange);
    this.addEventListener("variant-change", this.onVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener("quantity-change", this.onQuantityChange);
    this.removeEventListener("variant-change", this.onVariantChange);
  }

  onQuantityChange(e) {
    const { value } = e.detail;
    const input = this.querySelector('input[name="quantity"]');
    if (input) {
      input.value = value;
    }
  }

  onVariantChange(e) {
    if (!e.detail?.selectedOptions) return;

    this.lastFocusedSignature = this.getFocusedElementSignature();

    this.requestedURL = this.buildURL(e.detail.selectedOptions);

    this.loadProductInfo(this.requestedURL);
  }

  buildURL(options) {
    const params = [];
    if (options.length) {
      params.push(`option_values=${encodeURIComponent(options.join(","))}`);
    }

    return `${this.productUrl}?${params.join("&")}`;
  }

  loadProductInfo(url) {
    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        this.updateInfo(doc);
      })
      .catch((err) => {
        console.error("Error loading product info: ", err);
      })
  }

  updateInfo(doc) {
    const container = doc.querySelector("main-product");

    // UPDATE IMAGES
    const oldImages = this.querySelector(".main_product_media_wrapper") || this.querySelector('.featured_product_media_wrapper');
    const updatedImages = container.querySelector(".main_product_media_wrapper") || this.querySelector('.featured_product_media_wrapper');

    if (oldImages && updatedImages) {
      oldImages.innerHTML = updatedImages.innerHTML;
    }

    // UPDATE PRICE
    const oldPrice = this.querySelector(".main_product_price_wrapper") || this.querySelector(".featured_product_price_wrapper");
    const updatedPrice = container.querySelector(".main_product_price_wrapper") || this.querySelector(".featured_product_price_wrapper");

    if (oldPrice && updatedPrice) {
      oldPrice.innerHTML = updatedPrice.innerHTML;
    }

    // UPDATE SKU
    const oldSKU = this.querySelector('product-sku');
    const updatedSKU = container.querySelector('product-sku');

    if (oldSKU && updatedSKU) {
      oldSKU.innerHTML = updatedSKU.innerHTML;
    }

    // UPDATE INVENTORY
    const oldInventory = this.querySelector(".main_product_inventory_wrapper") || this.querySelector(".featured_product_inventory_wrapper");
    const updatedInventory = container.querySelector(".main_product_inventory_wrapper") || this.querySelector(".featured_product_inventory_wrapper");

    if (oldInventory && updatedInventory) {
      oldInventory.innerHTML = updatedInventory.innerHTML;
    }

    // UPDATE VARIANT SELECTOR
    const oldVariantSelector = this.querySelector(".main_product_variant_picker_wrapper") || this.querySelector(".featured_product_variant_picker_wrapper");
    const updatedVariantSelector = container.querySelector(".main_product_variant_picker_wrapper") || this.querySelector(".featured_product_variant_picker_wrapper");

    if (oldVariantSelector && updatedVariantSelector) {
      oldVariantSelector.innerHTML = updatedVariantSelector.innerHTML;

      if (this.updateUrl) {
        this.updateURL(oldVariantSelector)
      }
    }

    // UPDATE QUANTITY
    const oldQuantity = this.querySelector(".main_product_quantity_wrapper") || this.querySelector(".featured_product_quantity_wrapper");
    const updatedQuantity = container.querySelector(".main_product_quantity_wrapper") || this.querySelector(".featured_product_quantity_wrapper");

    if (oldQuantity && updatedQuantity) {
      oldQuantity.innerHTML = updatedQuantity.innerHTML;
    }

    // // UPDATE ACTION BUTTONS
    const oldActionBtns = this.querySelector(".main_product_action_buttons_wrapper") || this.querySelector(".featured_product_action_buttons_wrapper");
    const updatedActionBtns = container.querySelector(".main_product_action_buttons_wrapper") || this.querySelector(".featured_product_action_buttons_wrapper");

    if (oldActionBtns && updatedActionBtns) {
      oldActionBtns.innerHTML = updatedActionBtns.innerHTML;
    }

    // UPDATE PICKUP AVAILABILITY (preview + drawer together)
    const oldPickup = this.querySelector(
      ".main_product_pickup_availability_wrapper"
    );
    const newPickup = container.querySelector(
      ".main_product_pickup_availability_wrapper"
    );

    if (oldPickup && newPickup) {
      oldPickup.innerHTML = newPickup.innerHTML;
      oldPickup.classList.toggle('is-empty', newPickup.classList.contains('is-empty'));

      // re-bind drawer buttons for new DOM
      initDrawerOpenButtons();
    }

    const oldDrawer = this.querySelector(
      "pickup-availability-drawer"
    );
    const newDrawer = container.querySelector(
      "pickup-availability-drawer"
    );

    if (oldDrawer && newDrawer) {
      oldDrawer.innerHTML = newDrawer.innerHTML;
    }

    bindDrawerCloseButtons();

    const paDrawer = oldDrawer?.querySelector('.pickup_availability_drawer');
    if (paDrawer) {
      paDrawer.addEventListener("click", (event) => {
        const section = document.querySelector(".main-product-section");
        if (event.target === paDrawer) {
          if (section) {
            closeDrawer(paDrawer, section);
            RemoveScrollLock();
          } else {
            closeDrawer(paDrawer);
            RemoveScrollLock();
          }
        }
      });

      paDrawer.addEventListener("keydown", (event) => {
        const section = document.querySelector(".main-product-section");
        if (event.key === "Escape") {
          if (section) {
            closeDrawer(paDrawer, section);
            RemoveScrollLock();
          } else {
            closeDrawer(paDrawer);
            RemoveScrollLock();
          }
        }
      });
    }


    requestAnimationFrame(() => {
      this.restoreFocus(this.lastFocusedSignature);
    });
  }

  getFocusedElementSignature() {
    const el = document.activeElement;
    if (!this.contains(el)) return null;

    return {
      id: el.id || null,
      name: el.getAttribute("name"),
      value: el.value,
      dataset: { ...el.dataset },
      tag: el.tagName
    };
  }

  restoreFocus(signature) {
    if (!signature) return;

    let target = null;

    // Restore by ID (best)
    if (signature.id) {
      target = this.querySelector(`#${CSS.escape(signature.id)}`);
    }

    // Restore by name + value (inputs, radios)
    if (!target && signature.name) {
      target = this.querySelector(
        `[name="${CSS.escape(signature.name)}"][value="${CSS.escape(signature.value ?? "")}"]`
      );
    }

    // Restore by data attributes
    if (!target && signature.dataset) {
      for (const [key, val] of Object.entries(signature.dataset)) {
        target = this.querySelector(`[data-${key}="${CSS.escape(val)}"]`);
        if (target) break;
      }
    }

    if (target && typeof target.focus === "function") {
      target.focus({ preventScroll: true });
    }
  }

  updateURL(html) {
    const variantEl = html.querySelector('[data-selected-variant]');
    if (!variantEl) return; // unavailable variant → skip URL update

    let variantData;
    try {
      variantData = JSON.parse(variantEl.textContent);
    } catch {
      return;
    }

    if (!variantData?.id) return;

    window.history.replaceState(
      null,
      "",
      `${this.productUrl}?variant=${variantData.id}`
    );

  }
}

customElements.define("main-product", MainProduct);

class FeaturedProduct extends HTMLElement {
  constructor() {
    super();
    this.onQuantityChange = this.onQuantityChange.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.productUrl = this.dataset.productUrl;
    this.sectionId = this.dataset.sectionId;
    this.updateUrl = this.dataset.updateUrl === 'true';
  }

  connectedCallback() {
    this.addEventListener("quantity-change", this.onQuantityChange);
    this.addEventListener("variant-change", this.onVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener("quantity-change", this.onQuantityChange);
    this.removeEventListener("variant-change", this.onVariantChange);
  }

  onQuantityChange(e) {
    const { value } = e.detail;
    const input = this.querySelector('input[name="quantity"]');
    if (input) {
      input.value = value;
    }
  }

  onVariantChange(e) {
    if (!e.detail?.selectedOptions) return;

    this.lastFocusedSignature = this.getFocusedElementSignature();

    this.requestedURL = this.buildURL(e.detail.selectedOptions);

    this.loadProductInfo(this.requestedURL);
  }

  buildURL(options) {
    const params = [];
    if (options.length) {
      params.push(`option_values=${encodeURIComponent(options.join(","))}`);
    }

    if (this.sectionId) {
      params.push(`section_id=${this.sectionId}`);
    }

    return `${this.productUrl}?${params.join("&")}`;
  }

  loadProductInfo(url) {
    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, "text/html");
        this.updateInfo(doc);
      })
      .catch((err) => {
        console.error("Error loading product info: ", err);
      })
  }

  updateInfo(doc) {
    const container = doc.querySelector("featured-product");

    if(!container) return;

    // Capture old bg image src before wiping innerHTML
    const oldBgImg = this.querySelector('.featured_product_variant_bg_img img');
    const newBgImg = container.querySelector('.featured_product_variant_bg_img img');
    const bgIsChanging = newBgImg && oldBgImg && oldBgImg.src !== newBgImg.src;

    if (bgIsChanging) {
      const bgWrapper = this.querySelector('.featured_product_variant_bg_img');

      // Clone the new image, place it on top at opacity 0, then crossfade
      const incomingImg = newBgImg.cloneNode(true);
      incomingImg.classList.add('bg-img-incoming');
      bgWrapper.appendChild(incomingImg);

      // Force reflow so the browser registers opacity 0 before transitioning
      incomingImg.getBoundingClientRect();

      // Start both transitions simultaneously
      oldBgImg.classList.add('is-fading');
      incomingImg.classList.add('bg-img-visible');

      // After transition completes, swap innerHTML and clean up
      incomingImg.addEventListener('transitionend', () => {
        this.innerHTML = container.innerHTML;
        requestAnimationFrame(() => {
          this.restoreFocus(this.lastFocusedSignature);
        });
      }, { once: true });
    } else {
      this.innerHTML = container.innerHTML;
      // Fade in new bg image if one appeared for the first time
      const newSwappedImg = this.querySelector('.featured_product_variant_bg_img img');
      if (newSwappedImg && !oldBgImg) {
        newSwappedImg.classList.add('is-fading');
        newSwappedImg.getBoundingClientRect();
        newSwappedImg.classList.remove('is-fading');
      }
      requestAnimationFrame(() => {
        this.restoreFocus(this.lastFocusedSignature);
      });
    }
  }

  getFocusedElementSignature() {
    const el = document.activeElement;
    if (!this.contains(el)) return null;

    return {
      id: el.id || null,
      name: el.getAttribute("name"),
      value: el.value,
      dataset: { ...el.dataset },
      tag: el.tagName
    };
  }

  restoreFocus(signature) {
    if (!signature) return;

    let target = null;

    // Restore by ID (best)
    if (signature.id) {
      target = this.querySelector(`#${CSS.escape(signature.id)}`);
    }

    // Restore by name + value (inputs, radios)
    if (!target && signature.name) {
      target = this.querySelector(
        `[name="${CSS.escape(signature.name)}"][value="${CSS.escape(signature.value ?? "")}"]`
      );
    }

    // Restore by data attributes
    if (!target && signature.dataset) {
      for (const [key, val] of Object.entries(signature.dataset)) {
        target = this.querySelector(`[data-${key}="${CSS.escape(val)}"]`);
        if (target) break;
      }
    }

    if (target && typeof target.focus === "function") {
      target.focus({ preventScroll: true });
    }
  }

  updateURL(html) {
    const variantEl = html.querySelector('[data-selected-variant]');
    if (!variantEl) return; // unavailable variant → skip URL update

    let variantData;
    try {
      variantData = JSON.parse(variantEl.textContent);
    } catch {
      return;
    }

    if (!variantData?.id) return;

    window.history.replaceState(
      null,
      "",
      `${this.productUrl}?variant=${variantData.id}`
    );

  }
}

customElements.define("featured-product", FeaturedProduct);


// GIFT CARD FORM
if (!customElements.get("recipient-form")) {
  customElements.define(
    "recipient-form",
    class RecipientForm extends HTMLElement {
      constructor() {
        super();
        this.checkbox = this.querySelector(
          `#Recipient_checkbox--${this.dataset.sectionId}`,
        );
        this.checkbox.disabled = false;
        this.hiddenControls = this.querySelector(
          `#Recipient_control--${this.dataset.sectionId}`,
        );
        this.wrapper = this.querySelector(".recipients_input_wrapper");
        this.inner = this.wrapper.querySelector(".recipients_input_inner");
        this.hiddenControls.disabled = true;

        this.emailInput = this.querySelector(
          `#Recipient_email--${this.dataset.sectionId}`,
        );
        this.nameInput = this.querySelector(
          `#Recipient_name--${this.dataset.sectionId}`,
        );
        this.messageInput = this.querySelector(
          `#Recipient_message--${this.dataset.sectionId}`,
        );
        this.sendonInput = this.querySelector(
          `#Recipient_send_on--${this.dataset.sectionId}`,
        );
        this.offsetProperty = this.querySelector(
          `#Recipient_timezone_offset--${this.dataset.sectionId}`,
        );
        if (this.offsetProperty)
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();

        this.currentProductVariantId = this.dataset.variantId;
        this.addEventListener("change", this.onChange.bind(this));
      }

      connectedCallback() {
        const height = this.inner.clientHeight;
        this.wrapper.style.setProperty("--height", `${height}px`);
      }

      onChange() {
        if (this.checkbox.checked) {
          this.wrapper.setAttribute("open", "");
        } else {
          this.wrapper.removeAttribute("open");
        }
      }
    },
  );
}


// READ MORE
class ReadMore extends HTMLElement {
  constructor() {
    super();
    this.btn = this.querySelector('button');
  }

  connectedCallback() {
    if (!this.btn) return;
    this.rm = this.btn.querySelector('.rm');
    this.rl = this.btn.querySelector('.rl');
    this.btn.addEventListener('click', this.handleClick.bind(this));
    this.wrapper = this.previousElementSibling;
  }

  handleClick() {
    if (this.rm.classList.contains('active')) {
      this.rm.classList.remove('active');
      this.rl.classList.add('active');
      if (!this.wrapper) return;
      this.wrapper.classList.add('full');
    } else {
      this.rm.classList.add('active');
      this.rl.classList.remove('active');
      this.wrapper.classList.remove('full');
    }
  }
}

customElements.define('read-more', ReadMore);


// COLLAPSIBLE TAB
class CollapsibleTab extends HTMLElement {
  constructor() {
    super();

    this.btn = this.querySelector("button");
    this.content = this.querySelector(".collapsible_tab_content");
    this.inner = this.content?.querySelector(".collapsible_tab_content_inner");
    this.anchors = this.inner?.querySelectorAll("a");
    this.svg = this.btn?.querySelector("svg");

    this.handleClick = this.handleClick.bind(this);
    this.updateHeight = this.updateHeight.bind(this);
  }

  connectedCallback() {
    if (!this.btn || !this.content || !this.inner) return;

    this.btn.addEventListener("click", this.handleClick);

    if (this.anchors?.length) {
      this.anchors.forEach((a) => {
        a.setAttribute("inert", "");
      });
    }

    requestAnimationFrame(this.updateHeight);

    window.addEventListener("resize", this.updateHeight);
  }

  disconnectedCallback() {
    this.btn?.removeEventListener("click", this.handleClick);
    window.removeEventListener("resize", this.updateHeight);
  }

  updateHeight() {
    if (!this.inner || !this.content) return;

    requestAnimationFrame(() => {
      this.content.style.setProperty("--height", `${this.inner.scrollHeight}px`);
    });
  }

  handleClick() {
    const isOpen = this.content.hasAttribute("open");

    this.content.toggleAttribute("open", !isOpen);
    this.svg?.classList.toggle("rotate", !isOpen);

    if (this.anchors?.length) {
      this.anchors.forEach((a) => {
        a.toggleAttribute("inert", isOpen);
      });
    }

    this.updateHeight();
  }
}

customElements.define("collapsible-tab", CollapsibleTab);


// PRODUCT RECOMMENDATIONS
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();

    this.productId = this.dataset.productId;
    this.sectionId = this.dataset.sectionId;
    this.recommendedURL = this.dataset.url;
    this.type = this.dataset.type || "related";

    this.loaded = false;

    this.observer = new IntersectionObserver(this.handleObserver.bind(this), {
      rootMargin: "600px 0px", // load earlier
      threshold: 0.01
    });
  }

  connectedCallback() {
    if (!this.loaded) {
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.observer.disconnect();
  }

  handleObserver(entries, observer) {
    const entry = entries[0];

    if (!entry.isIntersecting || this.loaded) return;

    this.loaded = true;
    observer.unobserve(this);

    this.loadRecommendedProducts();
  }

  async loadRecommendedProducts() {
    try {
      const url = `${this.recommendedURL}&product_id=${this.productId}&section_id=${this.sectionId}`;

      const response = await fetch(url);
      const text = await response.text();

      const doc = new DOMParser().parseFromString(text, "text/html");
      const recommendations = doc.querySelector("product-recommendations");

      if (recommendations && recommendations.innerHTML.trim().length) {
        this.innerHTML = recommendations.innerHTML;

        this.productsInner =
          this.querySelector(".product_recommendations_inner") ||
          this.querySelector(".complementary_products_inner");

        document.dispatchEvent(
          new CustomEvent("recommended-products-updated")
        );
      }
    } catch (error) {
      console.error("Failed to load recommendations:", error);
    }
  }
}

customElements.define("product-recommendations", ProductRecommendations);


// PRODUCT DESKTOP MEDIA
class ProductDesktopMedia extends HTMLElement {
  constructor() {
    super();
    this.videos = this.querySelectorAll('video');
    this.youtubeFrames = this.querySelectorAll('iframe[src*="youtube.com"], iframe[src*="youtu.be"]');
    this.vimeoFrames = this.querySelectorAll('iframe[src*="vimeo.com"]');
    this.models = this.querySelectorAll('product-model');
  }

  connectedCallback() {
    if (this.videos.length) {
      this.videos.forEach((video) => {
        video.setAttribute('tabindex', '0');
        video.setAttribute('aria-label', 'Play/Pause video');
        video.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleVideoClick(video);
        });
        video.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleVideoClick(video);
          }
        });
      })
    }

    if (this.youtubeFrames.length) {
      this.youtubeFrames.forEach((youtubeFrame) => {
        youtubeFrame.nextElementSibling.addEventListener('click', () => {
          youtubeFrame.nextElementSibling.style.pointerEvents = 'none';
          this.handleYoutubeFrameClick(youtubeFrame);
        })
      })
    }

    if (this.vimeoFrames.length) {
      this.vimeoFrames.forEach((vimeoFrame) => {
        vimeoFrame.nextElementSibling.addEventListener('click', () => {
          vimeoFrame.nextElementSibling.style.pointerEvents = 'none';
          this.handleVimeoFrameClick(vimeoFrame);
        })
      })
    }

    if (this.models.length) {
      this.models.forEach((model) => {
        model.addEventListener('mouseenter', () => {
          lenis.stop();
          document.documentElement.style.overflow = 'auto';
        })

        model.addEventListener('mouseleave', () => {
          lenis.start();
          document.documentElement.style.overflow = 'unset';
        })

        model.addEventListener('click', () => this.handleProductModelClick(model));
      })
    }
  }

  handleVideoClick(video) {
    this.pauseAllMedia(video);
    video.nextElementSibling.classList.add('fade');
    if (video.paused) {
      video.play();
      video.nextElementSibling.classList.add('fade');
    } else {
      video.pause();
      video.nextElementSibling.classList.remove('fade');
    }
  }

  handleYoutubeFrameClick(youtubeFrame) {
    this.pauseAllMedia(youtubeFrame);
    youtubeFrame.contentWindow.postMessage(
      JSON.stringify({ event: "command", func: "playVideo", args: [] }),
      "*"
    );
  }

  handleVimeoFrameClick(vimeoFrame) {
    this.pauseAllMedia(vimeoFrame);
    vimeoFrame.contentWindow.postMessage(
      { method: "play" },
      "*"
    );
  }

  handleProductModelClick(model) {
    this.pauseAllMedia(model)
  }

  pauseAllMedia(except = null) {
    this.videos.forEach((video) => {
      if (video != except) {
        video.nextElementSibling.classList.remove('fade');
        video.pause();
      }
    })

    this.youtubeFrames.forEach((youtubeFrame) => {
      if (youtubeFrame !== except) {
        youtubeFrame.nextElementSibling.style.pointerEvents = 'all';
        youtubeFrame.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
          "*"
        );
      }
    });

    this.vimeoFrames.forEach((vimeoFrame) => {
      if (vimeoFrame !== except) {
        vimeoFrame.nextElementSibling.style.pointerEvents = 'all';
        vimeoFrame.contentWindow.postMessage({ method: "pause" }, "*");
      }
    });

    this.models.forEach((model) => {
      if (model !== except) {
        const modelViewer = model.querySelector('model-viewer');

        if (!modelViewer) return;
        if (model.modelViewerUI) model.modelViewerUI.pause();

        // Return to poster state visually
        const uiEl = model.querySelector('shopify-model-viewer-ui');
        if (uiEl) uiEl.classList.remove('shopify-model-viewer-ui--model-visible');
        const posterBtn = model.querySelector('.shopify-model-viewer-ui__button--poster');
        if (posterBtn) posterBtn.removeAttribute('hidden');
      }
    })
  }
}

customElements.define('product-desktop-media', ProductDesktopMedia);


// PRODUCT MOBILE MEDIA
class ProductMobileMedia extends HTMLElement {
  constructor() {
    super();

    this.videos = this.querySelectorAll('video');
    this.youtubeFrames = this.querySelectorAll('iframe[src*="youtube.com"], iframe[src*="youtu.be"]');
    this.vimeoFrames = this.querySelectorAll('iframe[src*="vimeo.com"]');
    this.models = this.querySelectorAll('product-model');
  }

  connectedCallback() {
    if (this.videos.length) {
      this.videos.forEach((video) => {
        video.setAttribute('tabindex', '0');
        video.setAttribute('aria-label', 'Play/Pause video');
        video.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleVideoClick(video);
        });
        video.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleVideoClick(video);
          }
        });
      })
    }

    if (this.youtubeFrames.length) {
      this.youtubeFrames.forEach((youtubeFrame) => {
        youtubeFrame.nextElementSibling.addEventListener('click', () => {
          youtubeFrame.nextElementSibling.style.pointerEvents = 'none';
          this.handleYoutubeFrameClick(youtubeFrame);
        })
      })
    }

    if (this.vimeoFrames.length) {
      this.vimeoFrames.forEach((vimeoFrame) => {
        vimeoFrame.nextElementSibling.addEventListener('click', () => {
          vimeoFrame.nextElementSibling.style.pointerEvents = 'none';
          this.handleVimeoFrameClick(vimeoFrame);
        })
      })
    }

    if (this.models.length) {
      this.models.forEach((model) => {
        model.addEventListener('mouseenter', () => {
          lenis.stop();
          document.documentElement.style.overflow = 'auto';
        })

        model.addEventListener('mouseleave', () => {
          lenis.start();
          document.documentElement.style.overflow = 'unset';
        })

        model.addEventListener('click', () => this.handleProductModelClick(model));
      })
    }

    this.mainSwiperEl = this.querySelector('.product_mobile_main_media.swiper');

    const waitForSwiper = () => {
      if (this.mainSwiperEl?.swiperInstance) {
        this.swiper = this.mainSwiperEl.swiperInstance;
        this.initModelControls();
      } else {
        requestAnimationFrame(waitForSwiper);
      }
    };

    waitForSwiper();

  }

  handleVideoClick(video) {
    this.pauseAllMedia(video);
    video.nextElementSibling.classList.add('fade');
    if (video.paused) {
      video.play();
      video.nextElementSibling.classList.add('fade');
    } else {
      video.pause();
      video.nextElementSibling.classList.remove('fade');
    }
  }

  handleYoutubeFrameClick(youtubeFrame) {
    this.pauseAllMedia(youtubeFrame);
    youtubeFrame.contentWindow.postMessage(
      JSON.stringify({ event: "command", func: "playVideo", args: [] }),
      "*"
    );
  }

  handleVimeoFrameClick(vimeoFrame) {
    this.pauseAllMedia(vimeoFrame);
    vimeoFrame.contentWindow.postMessage(
      { method: "play" },
      "*"
    );
  }

  initModelControls() {
    if (!this.models.length) return;

    this.models.forEach((model) => {

      model.addEventListener('touchstart', () => {
        this.disableSwiper();
      });

      model.addEventListener('mousedown', () => {
        this.disableSwiper();
      });

      model.addEventListener('touchend', () => {
        this.enableSwiper();
      });

      model.addEventListener('mouseup', () => {
        this.enableSwiper();
      });

      model.addEventListener('mouseleave', () => {
        this.enableSwiper();
      });

      model.addEventListener('touchstart', () => {
        this.disableSwiper();
        document.body.style.overflow = 'hidden';
      });

      model.addEventListener('touchend', () => {
        this.enableSwiper();
        document.body.style.overflow = '';
      });

      model.addEventListener('wheel', (e) => {
        e.preventDefault();
        e.stopPropagation();
      }, { passive: false });

      model.addEventListener('click', () => this.handleProductModelClick(model));
    });
  }

  handleProductModelClick(model) {
    this.pauseAllMedia(model)
  }

  disableSwiper() {
    if (!this.swiper) return;
    this.swiper.allowTouchMove = false;
  }

  enableSwiper() {
    if (!this.swiper) return;
    this.swiper.allowTouchMove = true;
  }

  pauseAllMedia(except = null) {
    this.videos.forEach((video) => {
      if (video != except) {
        video.nextElementSibling.classList.remove('fade');
        video.pause();
      }
    })

    this.youtubeFrames.forEach((youtubeFrame) => {
      if (youtubeFrame !== except) {
        youtubeFrame.nextElementSibling.style.pointerEvents = 'all';
        youtubeFrame.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
          "*"
        );
      }
    });

    this.vimeoFrames.forEach((vimeoFrame) => {
      if (vimeoFrame !== except) {
        vimeoFrame.nextElementSibling.style.pointerEvents = 'all';
        vimeoFrame.contentWindow.postMessage({ method: "pause" }, "*");
      }
    });

    this.models.forEach((model) => {
      if (model !== except) {
        const modelViewer = model.querySelector('model-viewer');

        if (!modelViewer) return;
        if (model.modelViewerUI) model.modelViewerUI.pause();

        // Return to poster state visually
        const uiEl = model.querySelector('shopify-model-viewer-ui');
        if (uiEl) uiEl.classList.remove('shopify-model-viewer-ui--model-visible');
        const posterBtn = model.querySelector('.shopify-model-viewer-ui__button--poster');
        if (posterBtn) posterBtn.removeAttribute('hidden');
      }
    })
  }
}

customElements.define('product-mobile-media', ProductMobileMedia);


// QUICK ADD PRODUCT MEDIA
class QuickAddProductMedia extends HTMLElement {
  constructor() {
    super();

    this.videos = this.querySelectorAll('video');
    this.youtubeFrames = this.querySelectorAll('iframe[src*="youtube.com"], iframe[src*="youtu.be"]');
    this.vimeoFrames = this.querySelectorAll('iframe[src*="vimeo.com"]');
    this.models = this.querySelectorAll('product-model');
  }

  connectedCallback() {
    if (this.videos.length) {
      this.videos.forEach((video) => {
        video.setAttribute('tabindex', '0');
        video.setAttribute('aria-label', 'Play/Pause video');
        video.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleVideoClick(video);
        });
        video.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleVideoClick(video);
          }
        });
      })
    }

    if (this.youtubeFrames.length) {
      this.youtubeFrames.forEach((youtubeFrame) => {
        youtubeFrame.nextElementSibling.addEventListener('click', () => {
          youtubeFrame.nextElementSibling.style.pointerEvents = 'none';
          this.handleYoutubeFrameClick(youtubeFrame);
        })
      })
    }

    if (this.vimeoFrames.length) {
      this.vimeoFrames.forEach((vimeoFrame) => {
        vimeoFrame.nextElementSibling.addEventListener('click', () => {
          vimeoFrame.nextElementSibling.style.pointerEvents = 'none';
          this.handleVimeoFrameClick(vimeoFrame);
        })
      })
    }

    if (this.models.length) {
      this.models.forEach((model) => {
        model.addEventListener('mouseenter', () => {
          lenis.stop();
          document.documentElement.style.overflow = 'auto';
        })

        model.addEventListener('mouseleave', () => {
          lenis.start();
          document.documentElement.style.overflow = 'unset';
        })

        model.addEventListener('click', () => this.handleProductModelClick(model));
      })
    }

    this.mainSwiperEl = this.querySelector('.product_mobile_main_media.swiper');

    const waitForSwiper = () => {
      if (this.mainSwiperEl?.swiperInstance) {
        this.swiper = this.mainSwiperEl.swiperInstance;
        this.initModelControls();
      } else {
        requestAnimationFrame(waitForSwiper);
      }
    };

    waitForSwiper();

  }

  handleVideoClick(video) {
    this.pauseAllMedia(video);
    video.nextElementSibling.classList.add('fade');
    if (video.paused) {
      video.play();
      video.nextElementSibling.classList.add('fade');
    } else {
      video.pause();
      video.nextElementSibling.classList.remove('fade');
    }
  }

  handleYoutubeFrameClick(youtubeFrame) {
    this.pauseAllMedia(youtubeFrame);
    youtubeFrame.contentWindow.postMessage(
      JSON.stringify({ event: "command", func: "playVideo", args: [] }),
      "*"
    );
  }

  handleVimeoFrameClick(vimeoFrame) {
    this.pauseAllMedia(vimeoFrame);
    vimeoFrame.contentWindow.postMessage(
      { method: "play" },
      "*"
    );
  }

  initModelControls() {
    if (!this.models.length) return;

    this.models.forEach((model) => {

      model.addEventListener('touchstart', () => {
        this.disableSwiper();
      });

      model.addEventListener('mousedown', () => {
        this.disableSwiper();
      });

      model.addEventListener('touchend', () => {
        this.enableSwiper();
      });

      model.addEventListener('mouseup', () => {
        this.enableSwiper();
      });

      model.addEventListener('mouseleave', () => {
        this.enableSwiper();
      });

      model.addEventListener('touchstart', () => {
        this.disableSwiper();
        document.body.style.overflow = 'hidden';
      });

      model.addEventListener('touchend', () => {
        this.enableSwiper();
        document.body.style.overflow = '';
      });

      model.addEventListener('wheel', (e) => {
        e.preventDefault();
        e.stopPropagation();
      }, { passive: false });

      model.addEventListener('click', () => this.handleProductModelClick(model));
    });
  }

  handleProductModelClick(model) {
    this.pauseAllMedia(model)
  }

  disableSwiper() {
    if (!this.swiper) return;
    this.swiper.allowTouchMove = false;
  }

  enableSwiper() {
    if (!this.swiper) return;
    this.swiper.allowTouchMove = true;
  }

  pauseAllMedia(except = null) {
    this.videos.forEach((video) => {
      if (video != except) {
        video.nextElementSibling.classList.remove('fade');
        video.pause();
      }
    })

    this.youtubeFrames.forEach((youtubeFrame) => {
      if (youtubeFrame !== except) {
        youtubeFrame.nextElementSibling.style.pointerEvents = 'all';
        youtubeFrame.contentWindow.postMessage(
          JSON.stringify({ event: "command", func: "pauseVideo", args: [] }),
          "*"
        );
      }
    });

    this.vimeoFrames.forEach((vimeoFrame) => {
      if (vimeoFrame !== except) {
        vimeoFrame.nextElementSibling.style.pointerEvents = 'all';
        vimeoFrame.contentWindow.postMessage({ method: "pause" }, "*");
      }
    });

    this.models.forEach((model) => {
      if (model !== except) {
        const modelViewer = model.querySelector('model-viewer');

        if (!modelViewer) return;
        if (model.modelViewerUI) model.modelViewerUI.pause();

        // Return to poster state visually
        const uiEl = model.querySelector('shopify-model-viewer-ui');
        if (uiEl) uiEl.classList.remove('shopify-model-viewer-ui--model-visible');
        const posterBtn = model.querySelector('.shopify-model-viewer-ui__button--poster');
        if (posterBtn) posterBtn.removeAttribute('hidden');
      }
    })
  }
}

customElements.define('quick-add-product-media', QuickAddProductMedia);

// SEARCH CLEAR BUTTON
class SearchClearButton extends HTMLElement {
  constructor() {
    super();
    this.form = this.closest('form[role="search"]');
    this.btn = this.querySelector('.search_clear_btn');    
  }

  connectedCallback() {
    this.btn.addEventListener('click', this.handleClick.bind(this));
  }

  handleClick(e) {
    e.preventDefault();
    const url = new URL(window.location.href);
    url.search = '';
    window.location.href = url.toString();
  }
}

customElements.define('search-clear-button', SearchClearButton);


// SCROLL TO TOP
class ScrollToTop extends HTMLElement {
  constructor() {
    super();    
  }

  connectedCallback() {
    window.addEventListener('scroll', () => {
      if (window.scrollY > 150) {
        this.classList.add("visible");
      } else {
        this.classList.remove("visible");
      }
    })

    this.addEventListener("click", () => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    });
  }
}

customElements.define('scroll-to-top', ScrollToTop);


// RECENTLY VIEWED
class RecentlyViewed extends HTMLElement {
  constructor() {
    super();

    this.storageKey = 'recently-viewed-products';
    this.sectionSelector = '[data-recently-viewed]';

    this.currentHandle = this.dataset.productHandle;
    this.maxProducts = Number(this.dataset.columns) || 4;
    this.aspectRatio = this.dataset.aspectRatio || 'portrait';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
  }

  connectedCallback() {
    if (!this.currentHandle) return;

    this.updateRecentlyViewed();
    this.displayRecentlyViewedProducts();
  }

  updateRecentlyViewed() {
    let viewed = JSON.parse(localStorage.getItem(this.storageKey)) || [];

    viewed = viewed.filter(handle => handle !== this.currentHandle);
    viewed.unshift(this.currentHandle);

    if (viewed.length > this.maxProducts) {
      viewed = viewed.slice(0, this.maxProducts);
    }

    localStorage.setItem(this.storageKey, JSON.stringify(viewed));
  }

  async displayRecentlyViewedProducts() {
    const section = document.querySelector(this.sectionSelector);
    if (!section) return;

    const handles = JSON.parse(localStorage.getItem(this.storageKey)) || [];

    if (!handles.length) {
      section.style.display = 'none';
      return;
    }

    try {
      const products = await Promise.all(
        handles.map(handle =>
          fetch(`/products/${handle}.js`).then(res => res.json())
        )
      );

      section.innerHTML = `
        <div class="recently_viewed_inner">
          ${products.map(product => this.renderProductCard(product)).join('')}
        </div>
      `;
    } catch (error) {
      console.error('Recently Viewed error:', error);
      section.style.display = 'none';
    }
  }

  renderProductCard(product) {
    if (!product) return '';

    const variant = product.variants[0];
    const price = variant.price;
    const compareAt = variant.compare_at_price;
    const hasSale = compareAt && compareAt > price;

    const image = product.images?.[0] || "";

    let ratio = 100;
    if (this.aspectRatio === 'portrait') ratio = 140;
    if (this.aspectRatio === 'square') ratio = 100;
    if (this.aspectRatio === 'adapt' && image) {
      ratio = 100;
    }

    return `
      <div class="recently_viewed_product">
        <div class="recently_viewed_product_img ratio" style="--aspect-ratio:${ratio}%">
          <a href="/products/${product.handle}" aria-label="${product.title}">
            ${image ? `<img src="${image}" alt="${product.title}" loading="lazy">` :
             '<svg class="placeholder_svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5"><path d="M375.5 345.2c0-.1 0-.1 0 0 0-.1 0-.1 0 0-1.1-2.9-2.3-5.5-3.4-7.8-1.4-4.7-2.4-13.8-.5-19.8 3.4-10.6 3.6-40.6 1.2-54.5-2.3-14-12.3-29.8-18.5-36.9-5.3-6.2-12.8-14.9-15.4-17.9 8.6-5.6 13.3-13.3 14-23 0-.3 0-.6.1-.8.4-4.1-.6-9.9-3.9-13.5-2.1-2.3-4.8-3.5-8-3.5h-54.9c-.8-7.1-3-13-5.2-17.5-6.8-13.9-12.5-16.5-21.2-16.5h-.7c-8.7 0-14.4 2.5-21.2 16.5-2.2 4.5-4.4 10.4-5.2 17.5h-48.5c-3.2 0-5.9 1.2-8 3.5-3.2 3.6-4.3 9.3-3.9 13.5 0 .2 0 .5.1.8.7 9.8 5.4 17.4 14 23-2.6 3.1-10.1 11.7-15.4 17.9-6.1 7.2-16.1 22.9-18.5 36.9-2.2 13.3-1.2 47.4 1 54.9 1.1 3.8 1.4 14.5-.2 19.4-1.2 2.4-2.3 5-3.4 7.9-4.4 11.6-6.2 26.3-5 32.6 1.8 9.9 16.5 14.4 29.4 14.4h176.8c12.9 0 27.6-4.5 29.4-14.4 1.2-6.5-.5-21.1-5-32.7zm-97.7-178c.3-3.2.8-10.6-.2-18 2.4 4.3 5 10.5 5.9 18h-5.7zm-36.3-17.9c-1 7.4-.5 14.8-.2 18h-5.7c.9-7.5 3.5-13.7 5.9-18zm4.5-6.9c0-.1.1-.2.1-.4 4.4-5.3 8.4-5.8 13.1-5.8h.7c4.7 0 8.7.6 13.1 5.8 0 .1 0 .2.1.4 3.2 8.9 2.2 21.2 1.8 25h-30.7c-.4-3.8-1.3-16.1 1.8-25zm-70.7 42.5c0-.3 0-.6-.1-.9-.3-3.4.5-8.4 3.1-11.3 1-1.1 2.1-1.7 3.4-2.1l-.6.6c-2.8 3.1-3.7 8.1-3.3 11.6 0 .2 0 .5.1.8.3 3.5.9 11.7 10.6 18.8.3.2.8.2 1-.2.2-.3.2-.8-.2-1-9.2-6.7-9.8-14.4-10-17.7 0-.3 0-.6-.1-.8-.3-3.2.5-7.7 3-10.5.8-.8 1.7-1.5 2.6-1.9h155.7c1 .4 1.9 1.1 2.6 1.9 2.5 2.8 3.3 7.3 3 10.5 0 .2 0 .5-.1.8-.3 3.6-1 13.1-13.8 20.1-.3.2-.5.6-.3 1 .1.2.4.4.6.4.1 0 .2 0 .3-.1 13.5-7.5 14.3-17.5 14.6-21.3 0-.3 0-.5.1-.8.4-3.5-.5-8.5-3.3-11.6l-.6-.6c1.3.4 2.5 1.1 3.4 2.1 2.6 2.9 3.5 7.9 3.1 11.3 0 .3 0 .6-.1.9-1.5 20.9-23.6 31.4-65.5 31.4h-43.8c-41.8 0-63.9-10.5-65.4-31.4zm91 89.1h-7c0-1.5 0-3-.1-4.2-.2-12.5-2.2-31.1-2.7-35.1h3.6c.8 0 1.4-.6 1.4-1.4v-14.1h2.4v14.1c0 .8.6 1.4 1.4 1.4h3.7c-.4 3.9-2.4 22.6-2.7 35.1v4.2zm65.3 11.9h-16.8c-.4 0-.7.3-.7.7 0 .4.3.7.7.7h16.8v2.8h-62.2c0-.9-.1-1.9-.1-2.8h33.9c.4 0 .7-.3.7-.7 0-.4-.3-.7-.7-.7h-33.9c-.1-3.2-.1-6.3-.1-9h62.5v9zm-12.5 24.4h-6.3l.2-1.6h5.9l.2 1.6zm-5.8-4.5l1.6-12.3h2l1.6 12.3h-5.2zm-57-19.9h-62.4v-9h62.5c0 2.7 0 5.8-.1 9zm-62.4 1.4h62.4c0 .9-.1 1.8-.1 2.8H194v-2.8zm65.2 0h7.3c0 .9.1 1.8.1 2.8H259c.1-.9.1-1.8.1-2.8zm7.2-1.4h-7.2c.1-3.2.1-6.3.1-9h7c0 2.7 0 5.8.1 9zm-7.7-66.7v6.8h-9v-6.8h9zm-8.9 8.3h9v.7h-9v-.7zm0 2.1h9v2.3h-9v-2.3zm26-1.4h-9v-.7h9v.7zm-9 3.7v-2.3h9v2.3h-9zm9-5.9h-9v-6.8h9v6.8zm-119.3 91.1c-2.1-7.1-3-40.9-.9-53.6 2.2-13.5 11.9-28.6 17.8-35.6 5.6-6.5 13.5-15.7 15.7-18.3 11.4 6.4 28.7 9.6 51.8 9.6h6v14.1c0 .8.6 1.4 1.4 1.4h5.4c.3 3.1 2.4 22.4 2.7 35.1 0 1.2.1 2.6.1 4.2h-63.9c-.8 0-1.4.6-1.4 1.4v16.1c0 .8.6 1.4 1.4 1.4H256c-.8 11.8-2.8 24.7-8 33.3-2.6 4.4-4.9 8.5-6.9 12.2-.4.7-.1 1.6.6 1.9.2.1.4.2.6.2.5 0 1-.3 1.3-.8 1.9-3.7 4.2-7.7 6.8-12.1 5.4-9.1 7.6-22.5 8.4-34.7h7.8c.7 11.2 2.6 23.5 7.1 32.4.2.5.8.8 1.3.8.2 0 .4 0 .6-.2.7-.4 1-1.2.6-1.9-4.3-8.5-6.1-20.3-6.8-31.1H312l-2.4 18.6c-.1.4.1.8.3 1.1.3.3.7.5 1.1.5h9.6c.4 0 .8-.2 1.1-.5.3-.3.4-.7.3-1.1l-2.4-18.6H333c.8 0 1.4-.6 1.4-1.4v-16.1c0-.8-.6-1.4-1.4-1.4h-63.9c0-1.5 0-2.9.1-4.2.2-12.7 2.3-32 2.7-35.1h5.2c.8 0 1.4-.6 1.4-1.4v-14.1h6.2c23.1 0 40.4-3.2 51.8-9.6 2.3 2.6 10.1 11.8 15.7 18.3 5.9 6.9 15.6 22.1 17.8 35.6 2.2 13.4 2 43.2-1.1 53.1-1.2 3.9-1.4 8.7-1 13-1.7-2.8-2.9-4.4-3-4.6-.2-.3-.6-.5-.9-.6h-.5c-.2 0-.4.1-.5.2-.6.5-.8 1.4-.3 2 0 0 .2.3.5.8 1.4 2.1 5.6 8.4 8.9 16.7h-42.9v-43.8c0-.8-.6-1.4-1.4-1.4s-1.4.6-1.4 1.4v44.9c0 .1-.1.2-.1.3 0 .1 0 .2.1.3v9c-1.1 2-3.9 3.7-10.5 3.7h-7.5c-.4 0-.7.3-.7.7 0 .4.3.7.7.7h7.5c5 0 8.5-.9 10.5-2.8-.1 3.1-1.5 6.5-10.5 6.5H210.4c-9 0-10.5-3.4-10.5-6.5 2 1.9 5.5 2.8 10.5 2.8h67.4c.4 0 .7-.3.7-.7 0-.4-.3-.7-.7-.7h-67.4c-6.7 0-9.4-1.7-10.5-3.7v-54.5c0-.8-.6-1.4-1.4-1.4s-1.4.6-1.4 1.4v43.8h-43.6c4.2-10.2 9.4-17.4 9.5-17.5.5-.6.3-1.5-.3-2s-1.5-.3-2 .3c-.1.2-1.4 2-3.2 5 .1-4.9-.4-10.2-1.1-12.8zm221.4 60.2c-1.5 8.3-14.9 12-26.6 12H174.4c-11.8 0-25.1-3.8-26.6-12-1-5.7.6-19.3 4.6-30.2H197v9.8c0 6.4 4.5 9.7 13.4 9.7h105.4c8.9 0 13.4-3.3 13.4-9.7v-9.8h44c4 10.9 5.6 24.5 4.6 30.2z"></path><path d="M286.1 359.3c0 .4.3.7.7.7h14.7c.4 0 .7-.3.7-.7 0-.4-.3-.7-.7-.7h-14.7c-.3 0-.7.3-.7.7zm5.3-145.6c13.5-.5 24.7-2.3 33.5-5.3.4-.1.6-.5.4-.9-.1-.4-.5-.6-.9-.4-8.6 3-19.7 4.7-33 5.2-.4 0-.7.3-.7.7 0 .4.3.7.7.7zm-11.3.1c.4 0 .7-.3.7-.7 0-.4-.3-.7-.7-.7H242c-19.9 0-35.3-2.5-45.9-7.4-.4-.2-.8 0-.9.3-.2.4 0 .8.3.9 10.8 5 26.4 7.5 46.5 7.5h38.1zm-7.2 116.9c.4.1.9.1 1.4.1 1.7 0 3.4-.7 4.7-1.9 1.4-1.4 1.9-3.2 1.5-5-.2-.8-.9-1.2-1.7-1.1-.8.2-1.2.9-1.1 1.7.3 1.2-.4 2-.7 2.4-.9.9-2.2 1.3-3.4 1-.8-.2-1.5.3-1.7 1.1s.2 1.5 1 1.7z"></path><path d="M275.5 331.6c-.8 0-1.4.6-1.5 1.4 0 .8.6 1.4 1.4 1.5h.3c3.6 0 7-2.8 7.7-6.3.2-.8-.4-1.5-1.1-1.7-.8-.2-1.5.4-1.7 1.1-.4 2.3-2.8 4.2-5.1 4zm5.4 1.6c-.6.5-.6 1.4-.1 2 1.1 1.3 2.5 2.2 4.2 2.8.2.1.3.1.5.1.6 0 1.1-.3 1.3-.9.3-.7-.1-1.6-.8-1.8-1.2-.5-2.2-1.2-3-2.1-.6-.6-1.5-.6-2.1-.1zm-38.2 12.7c.5 0 .9 0 1.4-.1.8-.2 1.3-.9 1.1-1.7-.2-.8-.9-1.3-1.7-1.1-1.2.3-2.5-.1-3.4-1-.4-.4-1-1.2-.8-2.4.2-.8-.3-1.5-1.1-1.7-.8-.2-1.5.3-1.7 1.1-.4 1.8.1 3.7 1.5 5 1.2 1.2 2.9 1.9 4.7 1.9z"></path><path d="M241.2 349.6h.3c.8 0 1.4-.7 1.4-1.5s-.7-1.4-1.5-1.4c-2.3.1-4.6-1.7-5.1-4-.2-.8-.9-1.3-1.7-1.1-.8.2-1.3.9-1.1 1.7.7 3.5 4.1 6.3 7.7 6.3zm-9.7 3.6c.2 0 .3 0 .5-.1 1.6-.6 3-1.6 4.2-2.8.5-.6.5-1.5-.1-2s-1.5-.5-2 .1c-.8.9-1.8 1.6-3 2.1-.7.3-1.1 1.1-.8 1.8 0 .6.6.9 1.2.9z"></path></svg>'
            }
          </a>
        </div>

        <div class="recently_viewed_product_info">
          <p>
            <a aria-label="${product.title}" href="/products/${product.handle}">${product.title}</a>
          </p>

          <div class="recently_viewed_product_price">
            <span class="${hasSale ? 'sale_p' : 'regular'}">
              ${Shopify.formatMoney(price, this.moneyFormat)}
            </span>
            ${hasSale ? `<s>${Shopify.formatMoney(compareAt, this.moneyFormat)}</s>` : ''}
          </div>
        </div>
      </div>
    `;
  }
}

customElements.define('recently-viewed', RecentlyViewed);


// FAQs
class FAQSection extends HTMLElement {
  constructor() {
    super();
    this.items = [...this.querySelectorAll('.faq_block')];
  }

  connectedCallback() {
    this.setHeights();

    this.items.forEach(item => {
      const question = item.querySelector('.faq_question');
      question?.addEventListener('click', () => this.toggle(item));
    });

    // Recalculate on resize (important)
    window.addEventListener('resize', () => this.setHeights());
  }

  setHeights() {
    this.items.forEach(item => {
      const answer = item.querySelector('.faq_answer');
      const inner = item.querySelector('.faq_answer_inner');

      if (!answer || !inner) return;

      // reset to auto to measure properly
      answer.style.height = 'auto';
      const height = inner.scrollHeight;

      // store in CSS variable
      answer.style.setProperty('--height', `${height}px`);

      // collapse again if not active
      if (!item.classList.contains('active')) {
        answer.style.height = '0px';
        answer.setAttribute('inert', '');
      } else {
        answer.removeAttribute('inert');
      }
    });
  }

  toggle(currentItem) {
  this.items.forEach(item => {
    const answer = item.querySelector('.faq_answer');
    const vert = item.querySelector('.faq_vert');
    const hori = item.querySelector('.faq_hori');

    if (!answer || !vert || !hori) return;

    if (item === currentItem) {
      const isOpen = item.classList.contains('active');

      if (isOpen) {
        item.classList.remove('active');
        answer.style.height = '0px';
        answer.setAttribute('inert', '');

        vert.classList.remove('rotate');
        hori.style.opacity = '1';

      } else {
        item.classList.add('active');
        answer.style.height = answer.style.getPropertyValue('--height');
        answer.removeAttribute('inert');

        vert.classList.add('rotate');
        hori.style.opacity = '0';
      }

    } else {
      item.classList.remove('active');
      answer.style.height = '0px';
      answer.setAttribute('inert', '');

      vert.classList.remove('rotate');
      hori.style.opacity = '1';
    }
  });
}
}

customElements.define('faq-section', FAQSection);


// SIZE CHART
document.addEventListener('click', (e) => {
  const sizeChartTrigger = e.target.closest('.size_chart_wrapper');
  const sizeChartPopup = document.querySelector('.size_chart_popup_wrapper');
  

  if(sizeChartTrigger && sizeChartPopup) {
    sizeChartPopup.classList.add('active');   
    document.body.setAttribute("data-scroll-locked", true);
    lenis.stop();     
    sizeChartPopup.removeAttribute('inert');
    sizeChartPopup.setAttribute('aria-hidden', 'false');
  }

  if(sizeChartPopup) {
    sizeChartPopup.addEventListener('click', () => {
      sizeChartPopup.classList.remove('active');     
      document.body.removeAttribute("data-scroll-locked");
        lenis.start();
        sizeChartPopup.setAttribute('inert', '');
        sizeChartPopup.setAttribute('aria-hidden', 'true');
    })

    const sizeChartInner = sizeChartPopup.querySelector('.size_chart_popup_inner');
    if(sizeChartInner) {
      sizeChartInner.addEventListener('click', (e) => {
        e.stopPropagation();
      })
    }

    const sizeChartClose = sizeChartPopup.querySelector('.size_chart_popup_close');
    if(sizeChartClose) {
      sizeChartClose.addEventListener('click', () => {
        sizeChartPopup.classList.remove('active');        
        document.body.removeAttribute("data-scroll-locked");
        lenis.start();
        sizeChartPopup.setAttribute('inert', '');
        sizeChartPopup.setAttribute('aria-hidden', 'true');
      })      
    }    
  }
})


// SIZE CHART
document.addEventListener('click', (e) => {
  const sizeChartTrigger = e.target.closest('.size_chart_wrapper');
  const sizeChartPopup = document.querySelector('.size_chart_popup_wrapper');
  

  if(sizeChartTrigger && sizeChartPopup) {
    sizeChartPopup.classList.add('active');   
    document.body.setAttribute("data-scroll-locked", true);
    lenis.stop();     
    sizeChartPopup.removeAttribute('inert');
    sizeChartPopup.setAttribute('aria-hidden', 'false');
  }

  if(sizeChartPopup) {
    sizeChartPopup.addEventListener('click', () => {
      sizeChartPopup.classList.remove('active');     
      document.body.removeAttribute("data-scroll-locked");
        lenis.start();
        sizeChartPopup.setAttribute('inert', '');
        sizeChartPopup.setAttribute('aria-hidden', 'true');
    })

    const sizeChartInner = sizeChartPopup.querySelector('.size_chart_popup_inner');
    if(sizeChartInner) {
      sizeChartInner.addEventListener('click', (e) => {
        e.stopPropagation();
      })
    }

    const sizeChartClose = sizeChartPopup.querySelector('.size_chart_popup_close');
    if(sizeChartClose) {
      sizeChartClose.addEventListener('click', () => {
        sizeChartPopup.classList.remove('active');        
        document.body.removeAttribute("data-scroll-locked");
        lenis.start();
        sizeChartPopup.setAttribute('inert', '');
        sizeChartPopup.setAttribute('aria-hidden', 'true');
      })      
    }    
  }
})

/* Featured collection tabs */
class FeaturedCollectionTabs extends HTMLElement{
  constructor(){
    super();
  }

  connectedCallback(){
    const section=this;

    if(!section)return;

    const tabs=section.querySelectorAll('.featured_collection_tab_btn');
    const contents=section.querySelectorAll('.featured_collection_tab_content');

    function setBannerHeight(){
      const productImg=section.querySelector('.product_card_img');
      const banners=section.querySelectorAll('.featured_collection_banner');

      if(!productImg||!banners.length)return;

      const productImgHeight=productImg.offsetHeight;

      banners.forEach((banner)=>{
        banner.style.height=productImgHeight+'px';
      });
    }

    function runSwiper(activeContent=null,resetSlide=false){
      const swiperSection=section.querySelector('swiper-section');

      if(!swiperSection)return;

      let targetSwiper=null;

      if(activeContent&&activeContent.classList.contains('swiper')){
        targetSwiper=activeContent;
      }else{
        targetSwiper=
          section.querySelector('.featured_collection_tab_content.active.swiper')||
          section.querySelector('.featured_collection_inner.swiper');
      }

      if(!targetSwiper)return;

      if(swiperSection.swiper){
        swiperSection.swiper.destroy(true,true);
        swiperSection.swiper=null;
      }

      swiperSection.swiperEl=targetSwiper;

      swiperSection.waitForSwiper().then(()=>{
        swiperSection.init();

        requestAnimationFrame(()=>{
          if(swiperSection.swiper){
            swiperSection.swiper.update();
            swiperSection.swiper.updateSize();
            swiperSection.swiper.updateSlides();
            swiperSection.swiper.updateProgress();
            swiperSection.swiper.updateSlidesClasses();

            if(resetSlide){
              swiperSection.swiper.slideTo(0,0);
            }
          }

          setBannerHeight();
        });
      });
    }

    function openTab(content,callback){
      content.classList.add('active');

      const height=content.scrollHeight;

      content.style.height='0px';

      requestAnimationFrame(()=>{
        content.style.height=height+'px';
      });

      let isDone=false;

      const done=()=>{
        if(isDone)return;
        isDone=true;

        content.removeEventListener('transitionend',handleTransitionEnd);
        content.style.height='auto';

        setBannerHeight();

        if(typeof callback==='function'){
          callback();
        }
      };

      const handleTransitionEnd=(event)=>{
        if(event.target!==content||event.propertyName!=='height')return;
        done();
      };

      content.addEventListener('transitionend',handleTransitionEnd);

      setTimeout(done,460);
    }

    function closeTab(content){
      content.style.height=content.scrollHeight+'px';

      requestAnimationFrame(()=>{
        content.style.height='0px';
      });

      setTimeout(()=>{
        content.classList.remove('active');
      },450);
    }

    runSwiper();

    setBannerHeight();

    window.addEventListener('resize',()=>{
      setBannerHeight();
    });

    if(!tabs.length)return;

    tabs.forEach((tab)=>{
      tab.addEventListener('click',()=>{
        tabs.forEach((btn)=>{
          btn.classList.remove('active');
        });

        tab.classList.add('active');

        const target=tab.dataset.tab;
        let activeContent=null;
        let wasAlreadyActive=false;

        contents.forEach((content)=>{
          const isActive=content.dataset.tabContent===target;

          if(isActive){
            activeContent=content;
            wasAlreadyActive=content.classList.contains('active');

            if(!wasAlreadyActive){
              openTab(content,()=>{
                runSwiper(content,true);

                setBannerHeight();
              });
            }
          }else{
            if(content.classList.contains('active')){
              closeTab(content);
            }
          }
        });

        const viewAllBtn=section.querySelector('[data-view-all-link]');

        if(viewAllBtn){
          const collectionUrl=tab.dataset.collectionUrl;

          if(collectionUrl){
            viewAllBtn.href=collectionUrl;
          }
        }

        if(wasAlreadyActive&&activeContent){
          runSwiper(activeContent,true);

          setBannerHeight();
        }
      });
    });

    setTimeout(()=>{
      setBannerHeight();
    },300);
  }
}

customElements.define('featured-collection-tabs',FeaturedCollectionTabs);

// Premium Hero Banner
class PremiumHeroBanner extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const section = this;
    if (!section) return;

    const swiperEl = section.querySelector('.swiper');
    if (!swiperEl) return;

    const waitForSwiper = () => {

      if (!swiperEl.swiperInstance) {
        requestAnimationFrame(waitForSwiper);
        return;
      }

      const swiper = swiperEl.swiperInstance;
      const duration = swiper.params.autoplay.delay;

      const animateBullet = () => {

        const bullets = section.querySelectorAll('.swiper-pagination-bullet');

        bullets.forEach((bullet) => {

          const progress = bullet.querySelector('.progress-ring__progress');

          if (!progress) return;

          progress.style.transition = 'none';
          progress.style.strokeDashoffset = '63';

          if (bullet.classList.contains('swiper-pagination-bullet-active')) {

            requestAnimationFrame(() => {

              requestAnimationFrame(() => {

                progress.style.transition = `stroke-dashoffset ${duration}ms linear`;
                progress.style.strokeDashoffset = '0';

              });

            });

          }

        });

      };

      swiper.on('init', animateBullet);
      swiper.on('slideChange', animateBullet);

      animateBullet();

    };

    waitForSwiper();
  }
}

customElements.define('premium-hero-banner', PremiumHeroBanner);

// LUXUARY COLLECTION GRID
class LuxuryCopyCode extends HTMLElement {
  constructor() {
    super();

    this.copyButtons = this.querySelectorAll('.luxury-copy-btn');

    this.init();
  }

  init() {
    if (!this.copyButtons.length) return;

    this.copyButtons.forEach((button) => {
      button.addEventListener('click', this.handleCopy.bind(this));
    });
  }

  async handleCopy(event) {
    const button = event.currentTarget;
    const code = button.getAttribute('data-code');

    try {
      await navigator.clipboard.writeText(code);

      const tooltip = button
        .closest('.luxury-copy-wrap')
        .querySelector('.luxury-copy-tooltip');

      if (!tooltip) return;

      const originalText = tooltip.innerText;

      tooltip.innerText = 'Copied!';

      setTimeout(() => {
        tooltip.innerText = originalText;
      }, 1800);

    } catch (error) {
      console.log('Copy failed');
    }
  }
}

customElements.define('luxury-copy-code', LuxuryCopyCode);

// Before & After
class BeforeAfter extends HTMLElement {
  constructor() {
    super();

    this.comparison = this.querySelector('[data-before-after]');
    if (!this.comparison) return;

    this.afterImage = this.comparison.querySelector('.after_image');
    this.handle = this.comparison.querySelector('.before_after_handle');
    this.dragger = this.comparison.querySelector('.before_after_dragger');

    this.isDragging = false;

    this.init();
  }

  init() {
    this.dragger.addEventListener('mousedown', this.startDrag.bind(this));
    this.dragger.addEventListener('touchstart', this.startDrag.bind(this), {
      passive: true,
    });

    document.addEventListener('mousemove', this.drag.bind(this));
    document.addEventListener('touchmove', this.drag.bind(this), {
      passive: false,
    });

    document.addEventListener('mouseup', this.stopDrag.bind(this));
    document.addEventListener('touchend', this.stopDrag.bind(this));
  }

  startDrag() {
    this.isDragging = true;
  }

  stopDrag() {
    this.isDragging = false;
  }

  drag(event) {
    if (!this.isDragging) return;

    const rect = this.comparison.getBoundingClientRect();

    let clientX;

    if (event.type.includes('touch')) {
      clientX = event.touches[0].clientX;
      event.preventDefault();
    } else {
      clientX = event.clientX;
    }

    let percentage = ((clientX - rect.left) / rect.width) * 100;

    percentage = Math.max(0, Math.min(100, percentage));

    this.afterImage.style.clipPath =
      `inset(0 ${100 - percentage}% 0 0)`;

    this.handle.style.left = `${percentage}%`;
  }
}

customElements.define('before-after', BeforeAfter);

